import { DEFAULT_RELIC_RUN_SOURCE_TEXT, DEFAULT_PICTIONARY_SOURCE_TEXT, DEFAULT_VAULT_SOURCE_TEXT } from "./gameSamples";
import { PLANET_HOPPERS_MODE } from "../../shared/planetHoppers";
import { RELIC_RUN_MODE, PICTURE_DETECTIVES_MODE, VAULT_BREAKERS_MODE, type RoomMode } from "../../shared/game";
import { ZIAMEZA_IDENTITY } from "../../shared/ziaMezaLinks";
import { isAccountLanguage, type AccountLanguage } from "../../shared/accountLanguage";
import { SITE_TUNING } from "./siteTuning";

export type Language = AccountLanguage;

const LANGUAGE_STORAGE_KEY = "ziarcadia:language";

const COPY = {
    // Keep all UI copy centralized so every future UI/UX label can be translated, including Korean.
    en: {
        siteTitle: "Ziarcadia — English games and classroom tools",
        siteSummary: "Ziarcadia has games for teaching English and useful tools for your classroom.",
        aboutZiarcadia: "About Ziarcadia",
        activityHistory: "History",
        historyPreviousMonth: "Previous month",
        historyNextMonth: "Next month",
        historyChooseMonth: "Choose month",
        historyToday: "Today",
        historyLoading: "Loading history…",
        historyLoadFailed: "History could not be loaded.",
        historyRetry: "Try again",
        historyRecordsSince: "Records since {date}",
        historyDayActivities: "Activities on the selected day",
        historyEmptyDay: "No activities recorded on this day.",
        historySourceMissing: "The activity was recorded, but its content was not saved.",
        historyLoadActivity: "Load activity",
        historyFinishGame: "Return the current game to its lobby before loading an activity.",
        musicClass: "Lyric Learn",
        musicSession: "Class session",
        musicEditLinks: "Edit links",
        musicSaveShort: "Save playlist",
        musicCurrentSong: "Current song",
        musicSongPosition: "{current} / {total}",
        musicRateSong: "What do you think?",
        musicRatingLow: "Not for me",
        musicRatingHigh: "Love it",
        musicEarlierSongs: "Earlier songs",
        musicKeepQueue: "Use this queue next time",
        musicTranslateShort: "Translate",
        musicTiming: "Line timing",
        musicSplit: "Video + lyrics",
        musicPlay: "Play",
        musicPause: "Pause",
        musicBackTen: "Back 10 seconds",
        musicForwardTen: "Forward 10 seconds",
        musicSeek: "Playback position",
        musicLyricSeek: "Scrub the song beside the lyrics",
        musicVolume: "Volume",
        musicSpeed: "Playback speed",
        musicPresent: "Present fullscreen",
        musicExitPresent: "Exit fullscreen",
        musicStudentRatings: "Ratings",
        musicSharedLyrics: "Reading with your class",
        musicFollow: "Follow song",
        musicFollowHint: "Keep the current lyric line in view",
        musicPlayLineAt: "Play this line at {time}",
        musicClassNote: "Watch, explore lyrics and rate songs",
        musicPlaylist: "Starting playlist",
        musicVideoSearch: "Search YouTube",
        musicVideoSearchSubmit: "Search",
        musicVideoSearchPlaceholder: "Song, artist or video",
        musicVideoSearchRequired: "Enter a song, artist or video name.",
        musicVideoSearching: "Searching YouTube…",
        musicVideoSearchEmpty: "No videos found. Try another search.",
        musicVideoSearchMore: "More videos",
        musicVideoInList: "In the list",
        musicVideoAdded: "Added to the end",
        musicVideoSearchUnavailable: "YouTube search is unavailable right now. Try again or paste a video link.",
        musicVideoSearchQuota: "YouTube search has reached its limit. You can still paste video links.",
        musicPaste: "Paste YouTube links — lines, spaces, commas or a copied list",
        musicImport: "Use this list",
        musicSave: "Save starting playlist",
        musicSaved: "Starting playlist saved",
        musicLocal: "Draft saved on this computer",
        musicClassLabel: "Class",
        musicYear: "School year",
        musicStart: "Open class",
        musicSongs: "{count} songs",
        musicSkipped: "Skipped: {duplicates} repeats · {invalid} invalid links · {overflow} over the 100-song limit",
        musicNoLinks: "No playable YouTube links found. Paste video links, including watch, Shorts or youtu.be links.",
        musicImportHint: "The list keeps its pasted order. Live suggestions stay in this class’s queue.",
        musicFinding: "Finding song names… {done}/{total}",
        musicSignIn: "Sign in to save playlists and class results.",
        musicLoginFailed: "Could not sign in. Try again.",
        musicSignInUnavailable: "Sign-in is unavailable here. Open Ziarcadia and sign in, then return.",
        musicConnected: "You’re connected",
        musicLookUp: "Watch the classroom screen",
        musicWaiting: "Your teacher will open song ratings here.",
        musicFaceDown: "Place your device face down until it’s time to rate the song.",
        musicName: "Your name",
        musicJoin: "Join class",
        musicRoomCode: "Room code",
        musicPeople: "{count} joined",
        musicParticipants: "Students",
        musicRatingProgress: "{rated}/{total} rated",
        musicOnPage: "On class page",
        musicAway: "Away",
        musicOffline: "Disconnected",
        musicPresenceUnknown: "No page signal",
        musicHasRated: "Rated",
        musicNeedsRating: "Waiting",
        musicNoParticipants: "No students yet",
        musicSeconds: "{count}s",
        musicMinutes: "{count}m",
        musicQueue: "Queue",
        musicVideo: "Video",
        musicLyrics: "Lyrics",
        musicReplay: "Replay",
        musicPrevious: "Previous song",
        musicNext: "Next song",
        musicOpenRatings: "Open ratings",
        musicRatingsOpen: "Ratings open",
        musicReveal: "Show results",
        musicHideResults: "Hide results",
        musicFinish: "Finish class",
        musicFinishDetail: "Students will no longer be able to change their ratings.",
        musicDone: "Class finished",
        musicAnother: "Another class",
        musicCompare: "Compare classes",
        musicAdd: "Add a suggestion",
        musicNextInQueue: "Play next",
        musicLastInQueue: "Add to end",
        musicAdded: "Added to this class’s queue",
        musicEditRating: "You can change any rating until class finishes.",
        musicRate: "Rate {song}",
        musicRatingSaved: "Rating saved",
        musicRatingSaving: "Saving…",
        musicNoRatings: "No ratings yet",
        musicVotes: "{count} votes",
        musicOneVote: "1 vote",
        musicResults: "Class results",
        musicRatingAverage: "{rating} out of 5 stars",
        musicRatingShare: "{rating} out of 5 — {votes}, {percent}%",
        musicNoResults: "No saved class results yet.",
        musicCompareHint: "Choose the class sessions to compare. The newest session for each class is selected first.",
        musicHistory: "Recent classes",
        musicResume: "Reopen",
        musicCopyJoin: "Copy join link",
        musicCopied: "Join link copied",
        musicMoveUp: "Move up",
        musicMoveDown: "Move down",
        musicRemove: "Remove song",
        musicLoading: "Loading…",
        musicLyricsLoading: "Finding lyrics…",
        musicLyricsMissing: "No confident lyric match. Try the song title and artist below.",
        musicSearchLyrics: "Find lyrics",
        musicSearchPlaceholder: "Song title and artist",
        musicOtherLyrics: "Choose another match",
        musicLyricSource: "Lyrics from LRCLIB",
        musicLyricHint: "Right-click a word, or select a phrase and choose Translate.",
        musicTranslate: "Translate selection",
        musicMeaning: "Meaning in this line",
        musicTranslateLoading: "Looking at the meaning…",
        musicSyncOffset: "Video intro (seconds)",
        musicSyncHint: "Line replay uses lyric timestamps plus this offset. Adjust for this video’s intro.",
        musicReplayLine: "Replay this line",
        musicLoopLine: "Loop this line",
        musicStopLoop: "Stop looping this line",
        musicSelectTerm: "Select a word or short phrase in the lyrics.",
        musicPlaybackFailed: "This video could not play here. Open it on YouTube or try another upload.",
        musicOpenYouTube: "Open on YouTube",
        musicReconnect: "Connection interrupted. Retrying…",
        musicUnsaved: "Playlist changed. Save it to use this order next time.",
        musicConflict: "The saved playlist changed elsewhere. Reload the saved version before saving again.",
        musicLoadSaved: "Load saved playlist",
        musicInvalidPlaylist: "Use 1–100 different YouTube videos.",
        musicClassRequired: "Enter the class and a four-digit school year.",
        musicFinished: "This class has finished. Ratings are now locked.",
        musicInvalidSong: "Choose a song in the queue.",
        musicAlreadyQueued: "These songs are already in the queue.",
        musicPlaylistFull: "The queue is full (100 songs).",
        musicInvalidAction: "That action is unavailable.",
        musicVotingClosed: "Your teacher has not opened ratings for this song yet.",
        musicInvalidRating: "Choose a rating from 1 to 5.",
        musicAccountChanged: "Your account changed. Reload your saved playlist.",
        musicRoomMissing: "That class could not be found. Check the room code.",
        musicRoomFull: "This class is full (80 students).",
        musicNameRequired: "Enter your name to join.",
        musicRejoin: "Join this class again to continue.",
        musicForbidden: "You do not have access to this class.",
        musicLookupFailed: "The lookup service is unavailable. Try again.",
        musicLyricsBusy: "The lyrics service is busy. Try again shortly, or use video captions.",
        musicVideoCaptions: "Video captions",
        musicTranscriptUnavailable: "Video captions are unavailable. Song lyrics are still available below.",
        musicTranscriptQuota: "Video captions have reached their request limit. Try song lyrics instead.",
        musicTranscriptTooLong: "This transcript is too long for the reading sheet.",
        musicRequestFailed: "Could not complete that action. Try again.",
        musicTranslationUnavailable: "Translation is unavailable right now. Try again.",
        musicSearchRequired: "Enter a song title and artist.",
        musicHistoryPartial: "Some saved sessions could not be loaded. Try again.",
        musicSaveBeforeStart: "Save this starting playlist first?",
        musicCancel: "Cancel",
        musicClose: "Close",
        musicRatingLabel: "{rating} out of 5",
        musicJoinTitle: "Join Lyric Learn",
        musicClassSession: "{className} · {year}",
        musicNoSong: "Add your starting playlist",
        phSearchImages: "Search images",
        phImageSource: "Image source",
        phImagesAllSources: "All sources",
        phImagesOpenverse: "Openverse",
        phImagesCommons: "Commons",
        phImagesWikipedia: "Wikipedia",
        phImageSearch: "Search",
        phMoreImages: "More images",
        phImageUrl: "Paste an image URL",
        phImageVideoUrl: "Paste an image or YouTube URL",
        phAddMediaLink: "Add link",
        phUploadImage: "Upload",
        phVideoClip: "YouTube clip",
        phVideoStart: "Start at (seconds)",
        phVideoEnd: "Stop at (optional)",
        phUseImage: "Use image: {title}",
        phImageSearching: "Searching…",
        phImageSourcesUnavailable: "Showing available results. {sources} is unavailable.",
        phImageResultCount: "{count} images",
        phNoImages: "No images found. Try another search.",
        phImageSearchUnavailable: "Search is unavailable. Upload or paste an image instead.",
        phImageAttaching: "Attaching image…",
        phImageAttachFailed: "This image could not be attached. Try another image or upload one.",
        phInvalidImageUrl: "Enter a valid HTTPS image URL.",
        phInvalidMediaUrl: "Enter a valid HTTPS image or YouTube URL.",
        phInvalidVideoTimes: "The stop time must be after the start time.",
        phQuizActions: "Quiz actions",
        phQuestionImage: "Question image",
        phAfterAnswer: "After answer",
        phQuestionActions: "Question actions",
        switchToEnglish: "English",
        switchToKorean: "한국어",
        toggleLanguageAria: "Toggle language",
        siteSubtitle: "Rooms for classroom relic runs",
        join: "Join",
        host: "Host",
        tools: "Tools",
        account: "Account",
        signIn: "Log in",
        signOut: "Sign out",
        hostAccount: "Host account",
        hostHistory: "Host history",
        wordLists: "Word lists",
        gameFiles: "Saved setups",
        setupSaveCopy: "Save setup",
        setupImport: "Import setup",
        setupCopy: "Copy setup",
        setupExport: "Download setup",
        setupSearch: "Find a saved setup",
        setupAccountAndLocal: "This computer · syncs with your account",
        setupLocalOnly: "Saved on this computer",
        setupSave: "Save",
        setupName: "Setup name",
        setupSavedLocal: "Saved on this computer",
        setupSavedAccount: "Saved to your account",
        setupSyncError: "Account sync unavailable. Your local save is safe.",
        setupStorageError: "Could not save this setup on this computer.",
        setupLibraryFull: "The library holds up to 100 setups.",
        setupEmpty: "Add game content before saving a setup.",
        setupPrevious: "Previous draft",
        setupOpened: "Setup loaded; your previous draft was kept.",
        setupOpen: "Open",
        setupClose: "Close",
        setupChooseMode: "Choose a game mode to see its setups.",
        setupImportSave: "Save imported setup",
        setupPaste: "Paste a Ziarcadia setup file",
        setupInvalid: "This setup file is invalid or unsupported.",
        setupCopied: "Setup copied",
        setupMyQuizzes: "My quizzes",
        setupMediaBackup: "Media backup",
        setupRestoreMedia: "Restore media",
        uploadedFiles: "Uploaded files",
        noHostedGamesYet: "No hosted games yet.",
        noWordListsYet: "No word lists yet.",
        noGameFilesYet: "No saved setups yet.",
        noUploadedFilesYet: "No uploaded files yet.",
        sourceUploaded: "Uploaded",
        sourcePlayed: "Used",
        lastPlayedSource: "Played setup",
        googleSignInUnavailable: "Google sign-in is not configured yet.",
        accountPlayerCount: "{count} players",
        hostedArchive: "Archive",
        fromComputer: "Computer",
        accountBack: "Back",
        name: "Name",
        roomCode: "Room code",
        placeName: "Enter your name",
        joinRoom: "Join a room",
        hostRoom: "Host a room",
        returnToSelection: "Return to selection",
        minimize: "Minimize",
        leave: "Leave",
        endGame: "Finish game & show results",
        finalResults: "Final Results",
        yourPlacement: "Your placement",
        continueToModes: "Choose next activity",
        waitingForHostContinue: "Waiting for host",
        hostChoosing: "Host is choosing...",
        noFinalResults: "No results yet",
        showPlayers: "Show players",
        hidePlayers: "Hide players",
        noPlayerResults: "No players yet",
        firstPlace: "1st",
        secondPlace: "2nd",
        thirdPlace: "3rd",
        finalResultNoPlacement: "Unplaced",
        finalResultBestTime: "Best time",
        finalResultScore: "Score",
        finalResultPointCount: "{count} pts",
        finalResultNoTime: "No finish",
        setup: "Setup",
        preview: "Preview",
        relicRun: "Relic Run",
        relicRunDescription: "Practice memorizing sentences in a temple. Choose the right words!",
        pictureDetectives: "Picture Detectives",
        pictureDetectivesDescription:
            "Pictionary-style drawing and guessing. Use clues to solve each case.",
        capybaraDetective: "Capybara detective",
        vaultBreakers: "Vault Breakers",
        vaultBreakersDescription:
            "Jeopardy-style team quiz. Answer together to crack classroom vaults.",
        planetHoppersDescription: "Answer Trivia in order to hop between planets.",
        pasteText: "Paste text",
        pasteVaults: "Paste vaults",
        pastePlaceholder: DEFAULT_RELIC_RUN_SOURCE_TEXT,
        vaultSourcePlaceholder: DEFAULT_VAULT_SOURCE_TEXT,
        vaultPreviewEmpty: "Paste categories and answers to preview the vault board.",
        fogVaultBoard: "Fog vault board",
        showVaultBoard: "Show vault board",
        categoryShort: "Cat",
        vaultScanner: "Scanner",
        vaultScannerButton: "Scan for More Information!",
        vaultAlarm: "Alarm",
        vaultReady: "Ready",
        vaultDone: "Done",
        vaultCrack: "Crack {number}",
        vaultResolveCrack: "Resolve crack",
        vaultResolvingIn: "Resolving in {seconds}",
        vaultDidntCrack: "Didn't crack it",
        vaultPlayerStatusScoping: "Scoping the scene...",
        rollChooser: "Roll",
        rollFirstStudent: "Roll the first student",
        vaultChooser: "Chooser",
        vaultChose: "The vault chose",
        vaultCrackTimer: "Crack timer",
        vaultCrackTimerSeconds: "{seconds}s crack timer",
        decreaseVaultCrackTimer: "Decrease crack timer",
        increaseVaultCrackTimer: "Increase crack timer",
        unlockNextVaultValue: "Unlock next vault value",
        vaultNextVault: "Next Vault!",
        vaultFinalVault: "Final Vault",
        vaultLastQuestion: "Last Question",
        vaultOneLastHeist: "One last heist!",
        vaultFinalOneCrackExplanation: "There's only one crack on this vault!",
        vaultFinalDetails: "1 crack · {seconds}s",
        vaultAwardFinalLabel: "Final Vault",
        vaultBankHeistedTitle: "The whole bank has been heisted!",
        vaultShowResults: "Show Results",
        vaultLockRank: "{rank} lock",
        vaultLockPosition: "{rank} lock",
        vaultLockPositionOf: "{rank} lock of {count}",
        vaultLockPositionCompact: "{rank}/{count}",
        vaultOtherLockOne: "1 Armed",
        vaultOtherLockMany: "{count} Armed",
        vaultMassiveLootLabel: "Massive Loot!",
        vaultDiamondLabel: "Diamond",
        vaultDiamondFoundLabel: "Diamond Found!",
        vaultDiamondEveryVaultPrefix: "Every",
        vaultDiamondEveryVaultSuffix: "vault is now",
        vaultDiamondFound: "{category} found diamonds! +{amount}!",
        vaultGrowingStashLabel: "Growing Stash",
        vaultGrowingStashFoundLabel: "Growing Stash!",
        vaultGrowingStashAllVaults: "All vaults {amount}",
        vaultGrowingStashNewTotal: "New total {amount} on each vault",
        vaultAwardCracked: "Cracked",
        vaultAwardLogged: "Effort Logged",
        vaultAwardBaseLabel: "Vault",
        vaultAwardMassiveLootLabel: "Massive Loot",
        vaultAwardDiamondLabel: "Diamond",
        vaultAwardQuickLabel: "Quick Lock",
        vaultAwardQuickLockRank: "{rank} lock of {count}",
        vaultAwardComebackLabel: "Comeback",
        vaultAwardEffortLabel: "Effort",
        vaultAwardBase: "Vault +{points}",
        vaultAwardQuick: "Quick Lock +{points}",
        vaultAwardComeback: "Comeback +{points}",
        vaultAwardEffort: "Effort +{points}",
        vaultTeamArmed: "Armed",
        vaultTeamAway: "Away",
        vaultTeamLeft: "Left",
        vaultTeamClosed: "Closed",
        vaultTeamCracking: "Cracking",
        vaultTeamDone: "Done",
        vaultTeamLogged: "Logged",
        vaultCrackProgress: "{count}/{total} cracks",
        wordPairList: "Word pairs",
        word: "Word",
        translation: "Translation",
        hint: "Hint",
        hintReveal: "Hint Reveal",
        pictionarySourcePlaceholder: DEFAULT_PICTIONARY_SOURCE_TEXT,
        sentenceStem: "Sentence stem",
        pictionaryStemPlaceholder: "I'm really interested in ____.",
        start: "Start",
        startingGame: "Starting...",
        nextWord: "Next case",
        endRoundNextWord: "Close case, next!",
        again: "Again",
        level: "Level",
        levelShort: "Lvl",
        levels: "Levels",
        team: "Team",
        teamBoard: "Team board",
        waitingRoom: "Waiting Room",
        waiting: "Waiting",
        mode: "Mode",
        players: "Players",
        joined: "Joined",
        playerList: "Player list",
        progress: "Progress",
        round: "Round",
        points: "Points",
        phQuestionPoints: "{count} points",
        yourPoints: "Your points",
        phScanFaster: "Hold to scan faster",
        phScanning: "Checking answer…",
        phTranslationShort: "Trans.",
        phTranslationPage: "Page translation detected",
        phTranslationContent: "Quiz text translation detected",
        phScanCorrect: "Scan complete: This is it!",
        phScanWrong: "Scan complete: wrong answer, try again",
        phHideAnswers: "Hide answer choices",
        phUnhideAnswers: "Unhide answer choices",
        enlargeImage: "Enlarge image",
        earnedPoints: "You earned {count} points",
        vaultPlayerTotal: "Total",
        vaultHoldToReviewAnswer: "Hold to reveal your answer",
        pointUnitShort: "pts",
        pointCountOne: "1 point",
        pointCountMany: "{count} points",
        adjustPoints: "Adjust points",
        addPoint: "Add point",
        subtractPoint: "Subtract point",
        worth: "Worth",
        stillGuessing: "Still Guessing",
        left: "Still Guessing",
        lives: "Tries",
        guess: "Guess!",
        guessWord: "Make a guess",
        guessPlaceholder: "Type your guess",
        guessed: "Solved",
        outOfLives: "No tries left",
        observeFirst: "Observe first",
        observeCountdown: "Observe {seconds}",
        speedBonus: "+{points} speed bonus!",
        speedBonusCelebration: "+{points} for speed!",
        pictionaryAwardCase: "Case",
        pictionaryAwardSpeed: "Speed",
        pictionaryAwardBreakthrough: "Breakthrough",
        pictionaryAwardEffort: "Effort",
        pictionaryAwardNeverQuit: "Never quit",
        pictionaryCelebrationSolved: "Case solved",
        pictionaryCelebrationSpeed: "Speed solve",
        pictionaryCelebrationBreakthrough: "Breakthrough",
        pictionaryCelebrationEffort: "Casework paid off",
        pictionaryCelebrationNeverQuit: "Never quit",
        breakthroughBonusAdvice: "Breakthrough possible: solve this case for a chance at a score bonus.",
        breakthroughEffortAdvice: "Casework logged: each checked guess raises your breakthrough chance.",
        breakthroughBonusCelebration: "Breakthrough +{points}!",
        detectiveNeverQuits: "A detective never quits!",
        addWords: "Add words",
        addVaults: "Add vaults",
        addWordPairs: "Add word pairs",
        needHostController: "Need a host controller",
        noTranslations: "No translations",
        noHints: "No hints",
        reveal: "Reveal",
        revealTimerSeconds: "Reveal Timer (s)",
        overTime: "Over time",
        overTimeShort: "Gradual",
        allAtOnce: "All at once",
        allAtOnceShort: "All",
        off: "Off",
        auto: "Auto",
        manual: "Manual",
        wordBank: "Case file",
        revealCaseTranslations: "Decode",
        caseTranslationsVisible: "Translations visible",
        caseTranslationsRecharging: "Translations recharging",
        reviewVocabulary: "Review words",
        returnToGame: "Return to game",
        previousWord: "Previous word",
        nextVocabularyWord: "Next word",
        expandCaseFile: "Expand case file",
        sameSentenceStem: "Same sentence stem",
        makeThisDeviceController: "This device is the controller",
        showTextAgain: "Show text again",
        passageTitle: "Passage",
        letsGo: "Let's go!",
        current: "Current",
        next: "Next",
        roomRoster: "Player list",
        currentStart: "Entrance",
        nextExit: "Relic",
        tileStart: "Enter",
        tileEnd: "Relic",
        tilePortal: "Descend",
        tileTrail: "Trail",
        tileCurrent: "You",
        escapedTitle: "You got the treasure!",
        escapedMessage: "The gold is yours.",
        beatPersonalBest: "Can you beat your personal best of {time}??",
        treasureHunting: "treasure hunting...",
        tryAgain: "Try again",
        noPlayersYet: "No players yet.",
        noOneHereYet: "No one is here yet.",
        noStudentsYet: "No students yet.",
        here: "Here",
        away: "Away",
        escapedShort: "Secured",
        copyCode: "Copy code",
        copyJoinLink: "Copy join link",
        copyHostLink: "Copy host link",
        copyConfirmed: "Copied!",
        viewerHost: "Host - {name}",
        viewerController: "Controller - {name}",
        viewerPlayer: "{name} / {roomCode}",
        watchingPlayer: "Watching {name}",
        defaultPlayerPlaceholder: "Student",
        defaultHostPlaceholder: "Teacher",
        close: "Close",
        save: "Save",
        scanToJoin: "Scan to join",
        showQrCodeToJoin: "Show QR code to join",
        showHostControllerQr: "Show host controller QR",
        controllerLabel: "Controller",
        controllerRoomCode: "Controller room code",
        switchRoom: "Switch room",
        switchRoomShort: "Go",
        fogCaseWords: "Fog case words",
        showCaseWords: "Show case words",
        hostControllerPurpose: "Scan this QR to let another device run the case controls.",
        hostControllerQrTitle: "Host Controller QR",
        tapQrToExpand: "Tap the QR to expand",
        treatAsOneLine: "Treat everything as one level",
        treatAsOneLevel: "Treat as 1 level",
        hostAlwaysPlays: "Host always plays",
        hostIsPlayerToo: "Host is a player too",
        hostController: "Host controller",
        hostControllerNeeded: "Need a host controller before you can start.",
        hostControllerReadyAs: "Host controller: {name}",
        waitingForController: "Waiting for the host controller.",
        waitingForControllerSeat: "Scan the controller QR to claim this seat.",
        waitForHostToStart: "Wait for the host to start",
        controllerLocked: "Controller locked",
        controllerLockedTo: "Locked to {name}",
        pickWord: "Pick the next case",
        waitingForWord: "Waiting for the case...",
        hostMustChooseNextWord: "The host is choosing a new case.",
        waitingForWordWithPoints: "Waiting for the case for {points}...",
        hostMustChooseNextWordWithPoints: "The host is choosing a new case for {points}.",
        usedCount: "x{count}",
        solvedRank: "#{rank} solved",
        livesRemainingOne: "1 try left",
        livesRemaining: "{count} tries left",
        noTeamsYet: "No teams yet.",
        clearCanvas: "Clear canvas",
        undo: "Undo",
        waitingMiniGameHint: "Collect pellets while you wait.",
        waitingMiniGameControls: "Drag, tap, or use arrow keys.",
        editName: "Edit name",
        editNamePrompt: "Enter a new name.",
        askRename: "Redo name",
        renameRequestPrompt: "Change your name, please.",
        renamePendingName: "Renaming self...",
        playerActions: "Player actions",
        playerListOptions: "Player list options",
        downloadPlayerHistory: "Download player history",
        playerHistoryEmpty: "No name history yet.",
        playerHistoryJoined: "Joined",
        playerHistoryReconnected: "Rejoined",
        playerHistoryRenamed: "Renamed",
        playerHistoryRenameRequested: "Redo requested",
        playerHistoryDisconnected: "Left",
        playerHistoryKicked: "Kicked",
        playerHistoryEvent: "Event",
        kick: "Kick",
        // Keep tool copy intentionally sparse; the interaction should explain itself.
        backToTools: "Back to tools",
        createRandomTeams: "Create Random Teams / Orders",
        randomTeamsNote: "Create equal random teams or orders using student ID's. You can move the players around.",
        createWordListTool: "Word List Builder",
        vocabularyReviewTool: "Vocabulary Review",
        vocabularyReviewNote: "Paste a word list and review English and Korean together.",
        vocabularyPaste: "Paste your words",
        vocabularyPlaceholder: "apple; 사과\nbicycle; 자전거\ncloud\n\nOne word or pair per line · tabs, commas or semicolons",
        vocabularyEmpty: "Your words will appear here.",
        vocabularyPreview: "Vocabulary preview",
        vocabularyTranslate: "Translate",
        vocabularyTranslating: "Translating…",
        vocabularyStart: "Review",
        vocabularyEdit: "Edit words",
        vocabularyUsePaperPalette: "Use paper colors",
        vocabularyUseDarkPalette: "Use dark colors",
        vocabularySavedBrowser: "Saved in this browser",
        vocabularyLoadingAccount: "Loading your saved list…",
        vocabularySavingAccount: "Saving to your account…",
        vocabularySavedAccount: "Saved to your account",
        vocabularySyncFailed: "Account sync unavailable. Your browser copy is kept.",
        vocabularyIdentityFailed: "Couldn't load your account. Keep this tab open and retry.",
        vocabularySyncConflict: "A newer list was saved on another device. Your edits are kept here.",
        vocabularySaveMine: "Save this list instead",
        vocabularyRetrySync: "Retry sync",
        vocabularyHide: "Hide translations",
        vocabularyShow: "Show translations",
        vocabularyTermTooLong: "To translate, keep each English entry under 160 characters.",
        vocabularyTranslationFailed: "Couldn't translate right now. Your list is saved. Try again.",
        wordListToolNote: "Paste English terms, generate Korean pairs or classroom prompts, then export the handout.",
        createAutotranslatorTool: "Autotranslator",
        autotranslatorNote: "Live Korean-English listening for Zia's phone.",
        autotranslatorReady: "Ready",
        autotranslatorConnecting: "Connecting",
        autotranslatorWarmingUp: "Warming up…",
        autotranslatorListening: "Listening",
        autotranslatorHearing: "Hearing",
        autotranslatorSettling: "Settling",
        autotranslatorPause: "{seconds}s pause",
        autotranslatorTableMode: "Table",
        autotranslatorNearMode: "Close",
        autotranslatorFarMode: "Room",
        autotranslatorFlip: "Flip",
        autotranslatorClear: "Clear",
        autotranslatorLevel: "Level",
        autotranslatorInputLevel: "Input level",
        autotranslatorInfo: "Info",
        autotranslatorDiagnosticOn: "Audio diagnostics · 7d",
        autotranslatorDiagnosticOff: "Audio diagnostics off",
        autotranslatorDiagnosticRecording: "Recording audio",
        autotranslatorDiagnosticSaving: "Saving audio",
        autotranslatorDiagnosticSaved: "Audio saved",
        autotranslatorDiagnosticUnavailable: "Audio diagnostics unavailable",
        autotranslatorDiagnosticLimit: "Audio saved · 5 min limit",
        autotranslatorDiagnosticRetention: "Diagnostic audio: up to 5 minutes per run. Deleted after 7 days or sooner after review. Tap to turn on or off.",
        autotranslatorDiagnosticDelete: "Delete diagnostic recordings",
        autotranslatorSessionInfo: "Session info",
        autotranslatorStart: "Start",
        autotranslatorStop: "Stop",
        autotranslatorAddNoun: "Names & terms",
        autotranslatorNounCount: "{count} saved names and terms",
        autotranslatorProperNouns: "Names & terms",
        autotranslatorNameEnglish: "English",
        autotranslatorNameKorean: "Korean",
        autotranslatorNameAdd: "Add",
        autotranslatorNameSave: "Save",
        autotranslatorNameCancel: "Cancel",
        autotranslatorNameEdit: "Edit {name}",
        autotranslatorNameRemove: "Remove {name}",
        autotranslatorNameEmpty: "Save a name or term in either language.",
        autotranslatorNameLimit: "40 names saved. Edit or remove one first.",
        autotranslatorNameConflict: "Another saved entry already uses this name.",
        autotranslatorNounPlaceholder: "Jisu / 지수",
        autotranslatorEmpty: "Ready",
        autotranslatorFollow: "Follow",
        autotranslatorFollowWithCount: "Follow +{count}",
        autotranslatorTranslating: "Translating...",
        autotranslatorTranslateFailed: "Translation failed.",
        autotranslatorRetryTranslation: "Retry translation",
        autotranslatorContextualTranslation: "Conversation meaning",
        autotranslatorHeardText: "Heard",
        autotranslatorLiteralTranslation: "Literal translation",
        autotranslatorContextInterpretation: "Context resolved",
        autotranslatorNoMicSupport: "Microphone access is not available in this browser.",
        autotranslatorStartFailed: "Could not start the live translator.",
        autotranslatorAudioNotReady: "Audio didn't start. Tap Start to try again.",
        autotranslatorConnectionLost: "Connection lost.",
        autotranslatorCostBasis: "List-price estimate before free tokens and credits",
        autotranslatorCostEstimatedRequests: "Usage estimated for {count} interrupted or pending requests.",
        autotranslatorPrivateOnly: "Autotranslator is private to ZiaMeza@gmail.com.",
        wordListTitleLabel: "Title",
        wordListModeLabel: "Output",
        wordListModeTranslation: "Translations",
        wordListModeHint: "Hints",
        wordListModeSentence: "Sentences",
        wordListModeCustom: "Custom",
        wordListContextNote: "<> can be used to add context to a word.",
        wordListDocumentOptionsLabel: "Document options",
        wordListAutoAlphabetize: "Autoalphabetize",
        wordListAutoCapitalize: "Autocapitalize",
        wordListGenerate: "Generate",
        wordListGenerating: "Generating...",
        wordListGeneratingButton: "Working",
        wordListDownloadDocx: "Word",
        wordListDownloadPdf: "PDF",
        wordListDownloadHwp: "HWP",
        wordListReadyStatus: "{count} terms",
        wordListPendingOutput: "...",
        wordListEmptyOutput: "Paste terms to begin.",
        wordListOutputPlaceholder: "Output",
        wordListPageLabel: "Page {number}",
        wordListGenerationFailed: "Generation failed.",
        wordListExportFailed: "Export failed.",
        playMaster1000: "Open Zia's Master 1000",
        master1000Note: `Learn 1000 common Korean words at ${ZIAMEZA_IDENTITY.displayDomain}.`,
        master1000LaunchNote: `Opens the live Master 1000 site on ${ZIAMEZA_IDENTITY.displayDomain}.`,
        master1000MissingTitle: "Zia's Master 1000 has moved",
        master1000MissingBody: "Open the live Master 1000 site at {path}.",
        randomTeamsModeTeams: "Teams",
        randomTeamsDefaultName: "team",
        randomTeamsGroupName: "Group name",
        randomTeamsSoundOnAction: "Turn sound on",
        randomTeamsSoundOffAction: "Turn sound off",
        randomTeamsAnimationsOnAction: "Show animations",
        randomTeamsAnimationsOffAction: "Skip animations",
        randomTeamsShowMissingAction: "Show missing IDs",
        randomTeamsHideMissingAction: "Hide missing IDs",
        randomTeamsClearAction: "Clear results",
        randomTeamsNamePrefix: "random",
        randomTeamsNameEnding: ".",
        randomTeamsMakeNamedGroups: "Make {groups}",
        randomTeamsNamedGroupLabel: "{groups}",
        randomTeamsNamedGroupSize: "{size} per {group}",
        randomTeamsNamedFloor: "No assigned {group}",
        randomTeamsModeOrder: "Order",
        randomTeamsMakeTeams: "Make teams",
        randomTeamsMakeOrders: "Make orders",
        randomTeamsShuffleAgain: "Shuffle again",
        randomTeamsShuffling: "Shuffling…",
        createVerb: "Create",
        randomTeamsSuffix: "random teams.",
        randomOrdersSuffix: "orders.",
        thereAre: "There are",
        thereIs: "There is",
        membersLabel: "members.",
        playersLabel: "players.",
        randomTeamsClassHas: "The class has",
        randomTeamsStudentsTotal: "students total",
        randomTeamsTodayStudentCount: "Today, there are {count} students",
        randomTeamsHereToday: "here today",
        randomTeamsAttendanceTotal: "total",
        randomTeamsAttendanceMissing: "missing",
        randomTeamsMissingIds: "Missing student IDs",
        randomTeamsSourceIds: "IDs",
        randomTeamsSourceNames: "Names",
        randomTeamsRosterInputLabel: "Student names",
        randomTeamsRosterPreview: "Parsed students",
        randomTeamsRosterSample: "Mina Park\n12 Jae Kim\nSora Lee, Alex Choi\n\nNames separated by lines or commas.\nStudent numbers are optional.",
        randomTeamsImportNames: "Import",
        randomTeamsRosterFileTypes: "Import TXT, CSV, TSV or Excel (.xlsx)",
        randomTeamsRosterEdit: "Edit list",
        randomTeamsRosterDone: "Done",
        randomTeamsRosterDuplicateId: "A student number appears more than once.",
        randomTeamsRosterInvalidId: "Student numbers must be between 1 and 9999.",
        randomTeamsRosterMissingName: "Add a name next to each student number.",
        randomTeamsRosterNoNames: "No names were found in that list.",
        randomTeamsRosterFileError: "Couldn't read that file. Choose a TXT, CSV, TSV, or XLSX file.",
        randomTeamsStudentNumber: "Student number {number}",
        randomTeamsGroupCount: "{count} teams",
        randomTeamsOneGroup: "1 team",
        randomTeamsGroupLabel: "teams",
        randomTeamsOneGroupLabel: "team",
        randomOrdersGroupLabel: "orders",
        randomOrdersOneGroupLabel: "order",
        randomOrdersGroupCount: "{count} orders",
        randomOrdersOneGroup: "1 order",
        randomTeamsGroupSize: "{size} per team",
        randomOrdersGroupSize: "{size} per order",
        randomTeamSuffixSingular: "random team.",
        randomTeamSuffixPlural: "random teams.",
        randomOrderSuffixSingular: "random order.",
        randomOrderSuffixPlural: "random orders.",
        studentCountLabelSingular: "student.",
        studentCountLabelPlural: "students.",
        exceptForStudent: "Except for student(s):",
        studentIdPlaceholder: "ID #",
        add: "Add",
        addStudentId: "Add ID",
        addStudentIdPrompt: "Enter a late student ID",
        removeStudentId: "Remove ID",
        removeStudentIdPrompt: "Enter an ID to pull out",
        submitStudentId: "Submit ID",
        removeItem: "Remove",
        included: "Included",
        excluded: "Excluded",
        randomTeamsWaitingTitle: "Press Start",
        randomTeamsReadyTitle: "Rearrange freely.",
        randomTeamsFloor: "No assigned team",
        randomTeamsFinished: "Finished",
        randomTeamsFloorHint: "Drop here to pull a number out of the teams.",
        randomTeamsExclusionError: "Use positive student IDs with no repeats.",
        randomTeamsDropHere: "Drop IDs here",
        randomTeamsDropNamesHere: "Drop names here",
        randomTeamsIdsInPlay: "{count} IDs in play",
        order: "Order",
        fullscreen: "Full screen",
        exitFullscreen: "Exit full screen",
        soundOn: "Sound on",
        soundOff: "Sound off",
        animationsOn: "Spawn animation on",
        animationsOff: "Spawn animation off",
        animationsAutoOff: "Spawn animation auto-off above 100 IDs",
        animationsReducedMotion: "Spawn animation off for reduced motion",
        showExcludedOnFloor: "Show excluded IDs on floor",
        hideExcludedOnFloor: "Hide excluded IDs on floor",
        randomTeamsFinishFront: "Finish first ID",
        wipe: "Wipe",
        randomTeamsLateAddInvalid: "Enter a new positive student ID.",
        randomTeamsLateAddDuplicate: "That ID is already in play.",
        randomTeamsLateAddExcluded: "Remove that ID from exceptions before adding it.",
        randomTeamsLateRemoveInvalid: "Enter a positive student ID.",
        randomTeamsLateRemoveMissing: "That ID isn't on the board.",
        inputSomeText: "Input some text",
        importTextFile: "Import .txt",
        importSourceFile: "Import file",
        translateSource: "Translate terms",
        translateAddMissing: "Add missing",
        translateReplaceAll: "Replace all",
        translateLoading: "Translating...",
        translateFailed: "Couldn't translate right now. Try again.",
        translateSourceChanged: "The text changed while translating. Try again.",
        textInput: "Text",
        tableView: "Table",
        editorView: "Editor view",
        underConstruction: "Under construction",
        category: "Category",
        value: "Value",
        prompt: "Prompt",
        answer: "Answer",
        info: "Info"
    },
    ko: {
        siteTitle: "지아르카디아 — 영어 수업 게임과 교실 도구",
        siteSummary: "지아르카디아는 영어 수업용 게임과 교실에서 쓰기 좋은 도구를 모아 둔 사이트예요.",
        aboutZiarcadia: "지아르카디아 소개",
        activityHistory: "활동 기록",
        historyPreviousMonth: "이전 달",
        historyNextMonth: "다음 달",
        historyChooseMonth: "월 선택",
        historyToday: "오늘",
        historyLoading: "기록을 불러오는 중…",
        historyLoadFailed: "기록을 불러오지 못했어요.",
        historyRetry: "다시 시도",
        historyRecordsSince: "{date}부터 기록이 있어요",
        historyDayActivities: "선택한 날의 활동",
        historyEmptyDay: "이 날에는 기록된 활동이 없어요.",
        historySourceMissing: "활동 기록은 있지만 내용은 저장되지 않았어요.",
        historyLoadActivity: "활동 불러오기",
        historyFinishGame: "활동을 불러오려면 현재 게임을 대기실로 돌려 주세요.",
        musicClass: "Lyric Learn",
        musicSession: "수업 설정",
        musicEditLinks: "링크 편집",
        musicSaveShort: "재생목록 저장",
        musicCurrentSong: "현재 곡",
        musicSongPosition: "{current} / {total}",
        musicRateSong: "이 노래, 어땠나요?",
        musicRatingLow: "내 취향은 아니에요",
        musicRatingHigh: "정말 좋아요",
        musicEarlierSongs: "이전 곡",
        musicKeepQueue: "다음에도 이 순서로 시작",
        musicTranslateShort: "번역",
        musicTiming: "가사 시간 조정",
        musicSplit: "영상 + 가사",
        musicPlay: "재생",
        musicPause: "일시 정지",
        musicBackTen: "10초 뒤로",
        musicForwardTen: "10초 앞으로",
        musicSeek: "재생 위치",
        musicLyricSeek: "가사 옆에서 노래 재생 위치 조절",
        musicVolume: "음량",
        musicSpeed: "재생 속도",
        musicPresent: "전체 화면으로 보기",
        musicExitPresent: "전체 화면 나가기",
        musicStudentRatings: "평점",
        musicSharedLyrics: "우리 반과 함께 읽기",
        musicFollow: "노래 따라가기",
        musicFollowHint: "현재 가사가 화면에 보이도록 따라가요",
        musicPlayLineAt: "{time}부터 이 가사 재생",
        musicClassNote: "뮤직비디오, 가사 살펴보기, 노래 평가",
        musicPlaylist: "시작 재생목록",
        musicVideoSearch: "YouTube 검색",
        musicVideoSearchSubmit: "검색",
        musicVideoSearchPlaceholder: "노래, 가수 또는 동영상",
        musicVideoSearchRequired: "노래, 가수 또는 동영상 이름을 입력하세요.",
        musicVideoSearching: "YouTube 검색 중…",
        musicVideoSearchEmpty: "동영상을 찾지 못했어요. 다른 검색어로 찾아보세요.",
        musicVideoSearchMore: "동영상 더 보기",
        musicVideoInList: "목록에 있음",
        musicVideoAdded: "목록 끝에 추가했어요",
        musicVideoSearchUnavailable: "지금은 YouTube 검색을 사용할 수 없어요. 다시 시도하거나 동영상 링크를 붙여 넣으세요.",
        musicVideoSearchQuota: "YouTube 검색 한도에 도달했어요. 동영상 링크는 계속 붙여 넣을 수 있어요.",
        musicPaste: "유튜브 링크 붙여넣기 — 줄바꿈, 공백, 쉼표, 복사한 목록 모두 가능",
        musicImport: "이 목록 사용",
        musicSave: "시작 재생목록 저장",
        musicSaved: "시작 재생목록을 저장했어요",
        musicLocal: "이 컴퓨터에 임시 저장됨",
        musicClassLabel: "학급",
        musicYear: "학년도",
        musicStart: "수업 열기",
        musicSongs: "{count}곡",
        musicSkipped: "제외: 중복 {duplicates}개 · 잘못된 링크 {invalid}개 · 100곡 초과 {overflow}개",
        musicNoLinks: "재생 가능한 유튜브 링크가 없어요. 일반 영상, Shorts, youtu.be 링크를 붙여넣어 주세요.",
        musicImportHint: "붙여넣은 순서대로 재생해요. 수업 중 추가한 추천곡은 해당 수업에만 들어가요.",
        musicFinding: "노래 정보를 찾는 중… {done}/{total}",
        musicSignIn: "재생목록과 학급별 결과를 저장하려면 로그인해 주세요.",
        musicLoginFailed: "로그인하지 못했어요. 다시 시도해 주세요.",
        musicSignInUnavailable: "여기서는 로그인할 수 없어요. Ziarcadia에서 로그인한 뒤 돌아와 주세요.",
        musicConnected: "접속했어요",
        musicLookUp: "교실 화면을 봐 주세요",
        musicWaiting: "선생님이 노래 평가를 열면 여기에 나타나요.",
        musicFaceDown: "노래를 평가할 때까지 기기를 화면이 아래로 향하게 놓아 주세요.",
        musicName: "이름",
        musicJoin: "수업 참여",
        musicRoomCode: "참여 코드",
        musicPeople: "{count}명 참여",
        musicParticipants: "학생",
        musicRatingProgress: "{rated}/{total}명 평가",
        musicOnPage: "수업 화면",
        musicAway: "다른 화면",
        musicOffline: "연결 끊김",
        musicPresenceUnknown: "화면 신호 없음",
        musicHasRated: "평가 완료",
        musicNeedsRating: "대기",
        musicNoParticipants: "아직 참여한 학생이 없어요",
        musicSeconds: "{count}초",
        musicMinutes: "{count}분",
        musicQueue: "재생 순서",
        musicVideo: "영상",
        musicLyrics: "가사",
        musicReplay: "다시 재생",
        musicPrevious: "이전 곡",
        musicNext: "다음 곡",
        musicOpenRatings: "평가 열기",
        musicRatingsOpen: "평가 중",
        musicReveal: "결과 공개",
        musicHideResults: "결과 숨기기",
        musicFinish: "수업 마치기",
        musicFinishDetail: "수업을 마치면 학생들이 평가를 바꿀 수 없어요.",
        musicDone: "수업이 끝났어요",
        musicAnother: "다른 학급 시작",
        musicCompare: "학급별 비교",
        musicAdd: "추천곡 추가",
        musicNextInQueue: "다음에 재생",
        musicLastInQueue: "맨 뒤에 추가",
        musicAdded: "이 수업의 재생목록에 추가했어요",
        musicEditRating: "수업이 끝날 때까지 이전 곡의 평가도 바꿀 수 있어요.",
        musicRate: "{song} 평가",
        musicRatingSaved: "평가를 저장했어요",
        musicRatingSaving: "저장 중…",
        musicNoRatings: "아직 평가가 없어요",
        musicVotes: "{count}표",
        musicOneVote: "1표",
        musicResults: "우리 반 결과",
        musicRatingAverage: "별 5개 중 {rating}개",
        musicRatingShare: "5점 중 {rating}점 — {votes}, {percent}%",
        musicNoResults: "아직 저장된 학급별 결과가 없어요.",
        musicCompareHint: "비교할 수업을 선택하세요. 학급별 가장 최근 수업이 먼저 선택돼요.",
        musicHistory: "최근 수업",
        musicResume: "다시 열기",
        musicCopyJoin: "참여 링크 복사",
        musicCopied: "참여 링크를 복사했어요",
        musicMoveUp: "위로 이동",
        musicMoveDown: "아래로 이동",
        musicRemove: "노래 삭제",
        musicLoading: "불러오는 중…",
        musicLyricsLoading: "가사를 찾는 중…",
        musicLyricsMissing: "맞는 가사를 찾지 못했어요. 아래에 곡명과 가수를 입력해 주세요.",
        musicSearchLyrics: "가사 찾기",
        musicSearchPlaceholder: "곡명과 가수",
        musicOtherLyrics: "다른 가사 선택",
        musicLyricSource: "가사 출처: LRCLIB",
        musicLyricHint: "단어를 오른쪽 클릭하거나 구절을 선택한 뒤 번역을 눌러 주세요.",
        musicTranslate: "선택한 부분 번역",
        musicMeaning: "이 문장에서의 뜻",
        musicTranslateLoading: "문맥에 맞는 뜻을 찾는 중…",
        musicSyncOffset: "영상 도입부 (초)",
        musicSyncHint: "가사 시간에 이 값을 더해 해당 부분을 재생해요. 영상 도입부에 맞게 조절하세요.",
        musicReplayLine: "이 줄 다시 듣기",
        musicLoopLine: "이 가사 반복 재생",
        musicStopLoop: "이 가사 반복 재생 중지",
        musicSelectTerm: "가사에서 단어나 짧은 구절을 선택해 주세요.",
        musicPlaybackFailed: "여기서는 영상을 재생할 수 없어요. 유튜브에서 열거나 다른 영상을 선택해 주세요.",
        musicOpenYouTube: "유튜브에서 열기",
        musicReconnect: "연결이 끊겼어요. 다시 연결하는 중…",
        musicUnsaved: "재생목록이 바뀌었어요. 다음에도 이 순서로 시작하려면 저장하세요.",
        musicConflict: "다른 곳에서 재생목록이 바뀌었어요. 저장된 목록을 다시 불러와 주세요.",
        musicLoadSaved: "저장된 재생목록 불러오기",
        musicInvalidPlaylist: "서로 다른 유튜브 영상 1~100개를 넣어 주세요.",
        musicClassRequired: "학급과 네 자리 학년도를 입력해 주세요.",
        musicFinished: "수업이 끝나 평가를 더 이상 바꿀 수 없어요.",
        musicInvalidSong: "재생목록에서 노래를 선택해 주세요.",
        musicAlreadyQueued: "이미 재생목록에 있는 노래예요.",
        musicPlaylistFull: "재생목록이 가득 찼어요 (100곡).",
        musicInvalidAction: "지금은 사용할 수 없는 기능이에요.",
        musicVotingClosed: "선생님이 아직 이 노래의 평가를 열지 않았어요.",
        musicInvalidRating: "1~5점 중에서 선택해 주세요.",
        musicAccountChanged: "계정이 바뀌었어요. 저장된 재생목록을 다시 불러와 주세요.",
        musicRoomMissing: "수업을 찾지 못했어요. 참여 코드를 확인해 주세요.",
        musicRoomFull: "참여 인원이 가득 찼어요 (80명).",
        musicNameRequired: "참여할 이름을 입력해 주세요.",
        musicRejoin: "계속하려면 수업에 다시 참여해 주세요.",
        musicForbidden: "이 수업에 접근할 권한이 없어요.",
        musicLookupFailed: "검색 서비스에 연결할 수 없어요. 다시 시도해 주세요.",
        musicLyricsBusy: "가사 서비스 요청이 많아요. 잠시 후 다시 시도하거나 동영상 자막을 이용해 주세요.",
        musicVideoCaptions: "동영상 자막",
        musicTranscriptUnavailable: "동영상 자막을 불러올 수 없어요. 아래에서 노래 가사를 찾아보세요.",
        musicTranscriptQuota: "동영상 자막 요청 한도에 도달했어요. 노래 가사를 이용해 주세요.",
        musicTranscriptTooLong: "이 자막은 읽기 화면에 표시하기에 너무 길어요.",
        musicRequestFailed: "처리하지 못했어요. 다시 시도해 주세요.",
        musicTranslationUnavailable: "지금은 번역할 수 없어요. 다시 시도해 주세요.",
        musicSearchRequired: "곡명과 가수를 입력해 주세요.",
        musicHistoryPartial: "일부 수업을 불러오지 못했어요. 다시 시도해 주세요.",
        musicSaveBeforeStart: "시작 재생목록을 먼저 저장할까요?",
        musicCancel: "취소",
        musicClose: "닫기",
        musicRatingLabel: "5점 만점에 {rating}점",
        musicJoinTitle: "Lyric Learn 참여",
        musicClassSession: "{year}학년도 · {className}반",
        musicNoSong: "시작 재생목록을 넣어 주세요",
        phSearchImages: "이미지 검색",
        phImageSource: "이미지 출처",
        phImagesAllSources: "모든 출처",
        phImagesOpenverse: "오픈버스",
        phImagesCommons: "위키미디어 공용",
        phImagesWikipedia: "위키백과",
        phImageSearch: "검색",
        phMoreImages: "이미지 더 보기",
        phImageUrl: "이미지 URL 붙여넣기",
        phImageVideoUrl: "이미지 또는 YouTube URL 붙여넣기",
        phAddMediaLink: "링크 추가",
        phUploadImage: "업로드",
        phVideoClip: "YouTube 재생 구간",
        phVideoStart: "시작 지점 (초)",
        phVideoEnd: "종료 지점 (선택)",
        phUseImage: "이미지 사용: {title}",
        phImageSearching: "검색 중…",
        phImageSourcesUnavailable: "검색 가능한 결과를 표시합니다. {sources} 검색은 현재 사용할 수 없습니다.",
        phImageResultCount: "이미지 {count}개",
        phNoImages: "이미지를 찾지 못했습니다. 다른 검색어를 입력하세요.",
        phImageSearchUnavailable: "검색을 사용할 수 없습니다. 이미지를 업로드하거나 붙여넣으세요.",
        phImageAttaching: "이미지 추가 중…",
        phImageAttachFailed: "이 이미지를 추가하지 못했습니다. 다른 이미지를 선택하거나 업로드하세요.",
        phInvalidImageUrl: "올바른 HTTPS 이미지 URL을 입력하세요.",
        phInvalidMediaUrl: "올바른 HTTPS 이미지 또는 YouTube URL을 입력하세요.",
        phInvalidVideoTimes: "종료 지점은 시작 지점보다 뒤여야 합니다.",
        phQuizActions: "퀴즈 작업",
        phQuestionImage: "문제 이미지",
        phAfterAnswer: "정답 공개 후",
        phQuestionActions: "문제 작업",
        switchToEnglish: "English",
        switchToKorean: "한국어",
        toggleLanguageAria: "언어 전환",
        siteSubtitle: "교실용 유물 탐험 방",
        join: "참가",
        host: "호스트",
        tools: "도구",
        name: "이름",
        roomCode: "방 코드",
        joinRoom: "방 참가하기",
        hostRoom: "방 만들기",
        returnToSelection: "모드 선택으로",
        minimize: "접기",
        leave: "나가기",
        endGame: "게임 끝내고 결과 보기",
        setup: "설정",
        preview: "미리보기",
        relicRun: "보물 탐험",
        relicRunDescription: "문장을 기억하며 보물을 찾아요",
        pictureDetectives: "그림 탐정단",
        pictureDetectivesDescription: "Pictionary-style 그림 맞히기로, 수사 파일의 단어 힌트를 활용해요",
        capybaraDetective: "카피바라 탐정",
        vaultBreakers: "금고털이",
        vaultBreakersDescription: "Jeopardy-style 팀 퀴즈로, 함께 답해 교실 금고를 열어요",
        planetHoppersDescription: "퀴즈에 답하며 행성 사이를 이동해요",
        pasteText: "텍스트 붙여넣기",
        pasteVaults: "금고 붙여넣기",
        pastePlaceholder: DEFAULT_RELIC_RUN_SOURCE_TEXT,
        vaultSourcePlaceholder: DEFAULT_VAULT_SOURCE_TEXT,
        vaultPreviewEmpty: "카테고리와 정답을 붙여넣으면 보드가 보여요.",
        fogVaultBoard: "금고판 가리기",
        showVaultBoard: "금고판 보기",
        categoryShort: "분류",
        vaultScanner: "스캐너",
        vaultScannerButton: "추가 정보 스캔!",
        vaultAlarm: "알람",
        vaultReady: "준비",
        vaultDone: "완료",
        vaultCrack: "{number}차 시도",
        vaultResolveCrack: "시도 확인",
        vaultResolvingIn: "{seconds}초 후 확인",
        vaultDidntCrack: "해독 실패",
        vaultPlayerStatusScoping: "상황 살피는 중...",
        rollChooser: "뽑기",
        rollFirstStudent: "첫 번째 학생 뽑기",
        vaultChooser: "선택자",
        vaultChose: "금고의 선택",
        vaultCrackTimer: "해독 시간",
        vaultCrackTimerSeconds: "해독 시간 {seconds}초",
        decreaseVaultCrackTimer: "해독 시간 줄이기",
        increaseVaultCrackTimer: "해독 시간 늘리기",
        unlockNextVaultValue: "다음 금고 금액 열기",
        vaultNextVault: "다음 금고!",
        vaultFinalVault: "최후의 금고",
        vaultLastQuestion: "마지막 문제",
        vaultOneLastHeist: "마지막 한탕!",
        vaultFinalOneCrackExplanation: "이 금고는 단 한 번만 도전할 수 있어요!",
        vaultFinalDetails: "단 한 번 · {seconds}초",
        vaultAwardFinalLabel: "최후의 금고",
        vaultBankHeistedTitle: "은행을 전부 털었어요!",
        vaultShowResults: "결과 보기",
        vaultLockRank: "{rank}번째 잠금",
        vaultLockPosition: "{rank}번째 잠금",
        vaultLockPositionOf: "{rank}/{count}번째 잠금",
        vaultLockPositionCompact: "{rank}/{count}",
        vaultOtherLockOne: "1명 잠금",
        vaultOtherLockMany: "{count}명 잠금",
        vaultMassiveLootLabel: "대박 보물!",
        vaultDiamondLabel: "다이아몬드",
        vaultDiamondFoundLabel: "다이아몬드 발견!",
        vaultDiamondEveryVaultPrefix: "모든",
        vaultDiamondEveryVaultSuffix: "금고가 이제",
        vaultDiamondFound: "{category}에 다이아몬드! +{amount}!",
        vaultGrowingStashLabel: "쌓이는 돈다발",
        vaultGrowingStashFoundLabel: "돈다발 발견!",
        vaultGrowingStashAllVaults: "모든 금고 {amount}",
        vaultGrowingStashNewTotal: "이제 금고마다 {amount}",
        vaultAwardCracked: "열림",
        vaultAwardLogged: "노력 기록",
        vaultAwardBaseLabel: "금고",
        vaultAwardMassiveLootLabel: "대박 보물",
        vaultAwardDiamondLabel: "다이아몬드",
        vaultAwardQuickLabel: "빠른 잠금",
        vaultAwardQuickLockRank: "{rank}/{count}번째 잠금",
        vaultAwardComebackLabel: "추격",
        vaultAwardEffortLabel: "노력",
        vaultAwardBase: "금고 +{points}",
        vaultAwardQuick: "빠른 잠금 +{points}",
        vaultAwardComeback: "추격 +{points}",
        vaultAwardEffort: "노력 +{points}",
        vaultTeamArmed: "잠금",
        vaultTeamAway: "자리 비움",
        vaultTeamLeft: "탭 나감",
        vaultTeamClosed: "마감",
        vaultTeamCracking: "해독 중",
        vaultTeamDone: "완료",
        vaultTeamLogged: "기록됨",
        vaultCrackProgress: "{count}/{total} 시도",
        wordPairList: "단어 짝",
        pictionarySourcePlaceholder: DEFAULT_PICTIONARY_SOURCE_TEXT,
        sentenceStem: "문장 틀",
        pictionaryStemPlaceholder: "I'm really interested in ____.",
        start: "시작",
        startingGame: "시작 중...",
        nextWord: "다음 사건",
        again: "다시",
        level: "레벨",
        levelShort: "레벨",
        levels: "레벨",
        team: "팀",
        teamBoard: "팀 보드",
        waitingRoom: "대기실",
        waiting: "대기 중",
        hostChoosing: "호스트가 고르는 중...",
        mode: "모드",
        players: "플레이어",
        joined: "참가",
        playerList: "플레이어 목록",
        progress: "진행도",
        round: "라운드",
        points: "점수",
        phQuestionPoints: "{count}점",
        yourPoints: "내 점수",
        phScanFaster: "길게 눌러 빠르게 스캔",
        phScanning: "정답인지 확인 중…",
        phTranslationShort: "번역",
        phTranslationPage: "페이지 번역 감지됨",
        phTranslationContent: "퀴즈 내용 번역 감지됨",
        phScanCorrect: "스캔 완료: 이게 정답이에요!",
        phScanWrong: "스캔 완료: 오답이에요. 다시 골라 보세요",
        phHideAnswers: "선택지 가리기",
        phUnhideAnswers: "선택지 다시 보기",
        enlargeImage: "이미지 확대",
        earnedPoints: "{count}점 획득",
        vaultPlayerTotal: "총점",
        vaultHoldToReviewAnswer: "길게 누르면 정답이 보여요",
        pointUnitShort: "점",
        pointCountOne: "1점",
        pointCountMany: "{count}점",
        worth: "배점",
        lives: "기회",
        guess: "맞히기!",
        guessWord: "추측하기",
        guessPlaceholder: "추측을 입력하세요",
        guessed: "해결",
        outOfLives: "기회 없음",
        speedBonus: "+{points} 속도 보너스!",
        speedBonusCelebration: "속도로 +{points}!",
        pictionaryAwardCase: "사건",
        pictionaryAwardSpeed: "속도",
        pictionaryAwardBreakthrough: "돌파",
        pictionaryAwardEffort: "노력",
        pictionaryAwardNeverQuit: "포기 안 함",
        pictionaryCelebrationSolved: "사건 해결",
        pictionaryCelebrationSpeed: "빠른 해결",
        pictionaryCelebrationBreakthrough: "돌파",
        pictionaryCelebrationEffort: "수사 성공",
        pictionaryCelebrationNeverQuit: "끝까지 해결",
        addWordPairs: "단어 짝 추가",
        addVaults: "금고 추가",
        needHostController: "호스트 컨트롤러 필요",
        showTextAgain: "본문 다시 보기",
        passageTitle: "본문",
        letsGo: "시작해요!",
        current: "현재",
        next: "다음",
        roomRoster: "플레이어 목록",
        currentStart: "입구",
        nextExit: "유물",
        tileStart: "입구",
        tileEnd: "유물",
        tilePortal: "하강",
        tileTrail: "지나감",
        tileCurrent: "나",
        escapedTitle: "보물을 찾았어요!",
        escapedMessage: "황금을 얻었어요.",
        tryAgain: "다시 하기",
        noPlayersYet: "아직 플레이어가 없어요.",
        noOneHereYet: "아직 아무도 없어요.",
        noStudentsYet: "아직 학생이 없어요.",
        here: "참가 중",
        away: "자리 비움",
        escapedShort: "획득 완료",
        copyCode: "코드 복사",
        copyJoinLink: "참가 링크 복사",
        copyHostLink: "호스트 링크 복사",
        copyConfirmed: "복사됨!",
        viewerHost: "호스트 - {name}",
        viewerController: "컨트롤러 - {name}",
        viewerPlayer: "{name} / {roomCode}",
        watchingPlayer: "{name} 관전 중",
        defaultPlayerPlaceholder: "학생",
        defaultHostPlaceholder: "교사",
        close: "닫기",
        save: "저장",
        scanToJoin: "스캔해서 참가",
        showQrCodeToJoin: "참가용 QR 코드 보기",
        showHostControllerQr: "호스트 컨트롤러 QR 보기",
        controllerRoomCode: "컨트롤러 방 코드",
        switchRoom: "방 바꾸기",
        switchRoomShort: "이동",
        fogCaseWords: "수사 파일 가리기",
        showCaseWords: "수사 파일 보기",
        hostControllerQrTitle: "호스트 컨트롤러 QR",
        tapQrToExpand: "QR을 눌러 크게 보기",
        treatAsOneLine: "모든 텍스트를 하나의 레벨로 보기",
        treatAsOneLevel: "1개 레벨로 처리",
        hostAlwaysPlays: "호스트도 항상 플레이",
        hostIsPlayerToo: "호스트도 플레이어로 참여",
        hostController: "호스트 컨트롤러",
        hostControllerNeeded: "시작하려면 호스트 컨트롤러가 필요해요.",
        hostControllerReadyAs: "호스트 컨트롤러: {name}",
        waitingForController: "호스트 컨트롤러를 기다리는 중이에요.",
        waitingForControllerSeat: "컨트롤러 QR을 스캔해 연결하세요.",
        controllerLocked: "컨트롤러 잠김",
        controllerLockedTo: "{name}님이 컨트롤러를 사용 중이에요",
        pickWord: "다음 사건을 고르세요",
        usedCount: "x{count}",
        solvedRank: "#{rank}번째 정답",
        livesRemainingOne: "기회 1번 남음",
        livesRemaining: "기회 {count}번 남음",
        noTeamsYet: "아직 팀이 없어요.",
        clearCanvas: "캔버스 지우기",
        undo: "되돌리기",
        waitingMiniGameHint: "기다리는 동안 구슬을 모아 보세요.",
        waitingMiniGameControls: "드래그, 탭 또는 방향키를 사용하세요.",
        editName: "이름 바꾸기",
        editNamePrompt: "새 이름을 입력하세요.",
        askRename: "이름 다시 입력",
        renameRequestPrompt: "이름을 바꿔 주세요.",
        renamePendingName: "이름 변경 중...",
        playerActions: "플레이어 작업",
        playerListOptions: "플레이어 목록 옵션",
        downloadPlayerHistory: "플레이어 기록 다운로드",
        playerHistoryEmpty: "아직 이름 기록이 없어요.",
        playerHistoryJoined: "입장",
        playerHistoryReconnected: "재입장",
        playerHistoryRenamed: "이름 변경",
        playerHistoryRenameRequested: "이름 요청",
        playerHistoryDisconnected: "나감",
        playerHistoryKicked: "내보냄",
        playerHistoryEvent: "기록",
        backToTools: "도구로 돌아가기",
        createRandomTeams: "랜덤 팀 / 순서 만들기",
        randomTeamsNote: "학생 번호로 균등한 랜덤 팀이나 순서를 만들고 자유롭게 옮길 수 있어요.",
        createWordListTool: "단어 목록 제작",
        vocabularyReviewTool: "단어 복습",
        vocabularyReviewNote: "단어 목록을 붙여넣고 영어와 한국어를 함께 복습해요.",
        vocabularyPaste: "단어 붙여넣기",
        vocabularyPlaceholder: "apple; 사과\nbicycle; 자전거\ncloud\n\n한 줄에 단어 또는 단어 쌍 · 탭, 쉼표, 세미콜론으로 구분",
        vocabularyEmpty: "단어가 여기에 표시돼요.",
        vocabularyPreview: "단어 미리보기",
        vocabularyTranslate: "번역",
        vocabularyTranslating: "번역 중…",
        vocabularyStart: "복습하기",
        vocabularyEdit: "단어 수정",
        vocabularyUsePaperPalette: "종이 색상 사용",
        vocabularyUseDarkPalette: "어두운 색상 사용",
        vocabularySavedBrowser: "이 브라우저에 저장됨",
        vocabularyLoadingAccount: "저장된 목록을 불러오는 중…",
        vocabularySavingAccount: "계정에 저장 중…",
        vocabularySavedAccount: "계정에 저장됨",
        vocabularySyncFailed: "계정에 연결할 수 없어요. 이 브라우저의 목록은 보관돼요.",
        vocabularyIdentityFailed: "계정 정보를 불러오지 못했어요. 이 탭을 열어 둔 채 다시 시도해 주세요.",
        vocabularySyncConflict: "다른 기기에 더 최신 목록이 있어요. 수정한 내용은 여기에 보관돼요.",
        vocabularySaveMine: "이 목록으로 저장",
        vocabularyRetrySync: "다시 동기화",
        vocabularyHide: "번역 숨기기",
        vocabularyShow: "번역 보기",
        vocabularyTermTooLong: "번역하려면 각 영어 항목을 160자 이하로 입력해 주세요.",
        vocabularyTranslationFailed: "지금 번역할 수 없어요. 목록은 저장되어 있어요. 다시 시도해 주세요.",
        wordListToolNote: "영어 단어를 붙여넣고 번역, 힌트 또는 문장을 만든 뒤 학습지로 내보내요.",
        createAutotranslatorTool: "자동 번역기",
        autotranslatorNote: "Zia 휴대폰에서 한국어와 영어를 실시간으로 듣고 번역해요.",
        autotranslatorReady: "준비",
        autotranslatorConnecting: "연결 중",
        autotranslatorWarmingUp: "준비 중…",
        autotranslatorListening: "듣는 중",
        autotranslatorHearing: "음성 감지",
        autotranslatorSettling: "정리 중",
        autotranslatorPause: "{seconds}초 동안 멈춤",
        autotranslatorTableMode: "테이블",
        autotranslatorNearMode: "가까이",
        autotranslatorFarMode: "교실",
        autotranslatorFlip: "뒤집기",
        autotranslatorClear: "지우기",
        autotranslatorLevel: "레벨",
        autotranslatorInputLevel: "입력 레벨",
        autotranslatorInfo: "정보",
        autotranslatorDiagnosticOn: "진단 오디오 · 7일",
        autotranslatorDiagnosticOff: "진단 오디오 꺼짐",
        autotranslatorDiagnosticRecording: "오디오 녹음 중",
        autotranslatorDiagnosticSaving: "오디오 저장 중",
        autotranslatorDiagnosticSaved: "오디오 저장됨",
        autotranslatorDiagnosticUnavailable: "진단 오디오 사용 불가",
        autotranslatorDiagnosticLimit: "오디오 저장됨 · 5분 제한",
        autotranslatorDiagnosticRetention: "진단 오디오: 회당 최대 5분. 7일 후 또는 검토 완료 후 더 일찍 삭제됩니다. 눌러 켜거나 끄세요.",
        autotranslatorDiagnosticDelete: "진단 녹음 삭제",
        autotranslatorSessionInfo: "세션 정보",
        autotranslatorStart: "시작",
        autotranslatorStop: "중지",
        autotranslatorAddNoun: "이름과 용어",
        autotranslatorNounCount: "저장된 이름과 용어 {count}개",
        autotranslatorProperNouns: "이름과 용어",
        autotranslatorNameEnglish: "영어",
        autotranslatorNameKorean: "한국어",
        autotranslatorNameAdd: "추가",
        autotranslatorNameSave: "저장",
        autotranslatorNameCancel: "취소",
        autotranslatorNameEdit: "{name} 수정",
        autotranslatorNameRemove: "{name} 삭제",
        autotranslatorNameEmpty: "어느 언어로든 이름이나 용어를 저장하세요.",
        autotranslatorNameLimit: "40개가 저장되었습니다. 먼저 수정하거나 삭제하세요.",
        autotranslatorNameConflict: "다른 저장 항목에서 이미 사용하는 이름입니다.",
        autotranslatorNounPlaceholder: "Jisu / 지수",
        autotranslatorEmpty: "준비",
        autotranslatorFollow: "따라가기",
        autotranslatorFollowWithCount: "따라가기 +{count}",
        autotranslatorTranslating: "번역 중...",
        autotranslatorTranslateFailed: "번역 실패.",
        autotranslatorRetryTranslation: "번역 다시 시도",
        autotranslatorContextualTranslation: "대화 맥락 번역",
        autotranslatorHeardText: "들은 내용",
        autotranslatorLiteralTranslation: "직역",
        autotranslatorContextInterpretation: "맥락으로 해석",
        autotranslatorNoMicSupport: "이 브라우저에서는 마이크를 사용할 수 없어요.",
        autotranslatorStartFailed: "실시간 번역기를 시작할 수 없어요.",
        autotranslatorAudioNotReady: "오디오를 시작하지 못했어요. 시작을 다시 눌러 주세요.",
        autotranslatorConnectionLost: "연결이 끊겼어요.",
        autotranslatorCostBasis: "무료 토큰과 크레딧 적용 전 정가 추정",
        autotranslatorCostEstimatedRequests: "중단되었거나 처리 중인 요청 {count}개의 사용량은 추정치입니다.",
        autotranslatorPrivateOnly: "자동 번역기는 ZiaMeza@gmail.com 전용이에요.",
        wordListTitleLabel: "제목",
        wordListModeLabel: "출력",
        wordListModeTranslation: "번역",
        wordListModeHint: "힌트",
        wordListModeSentence: "문장",
        wordListModeCustom: "맞춤",
        wordListContextNote: "<>로 단어에 맥락을 더할 수 있어요.",
        wordListDocumentOptionsLabel: "문서 옵션",
        wordListAutoAlphabetize: "자동 알파벳순",
        wordListAutoCapitalize: "자동 대문자화",
        wordListGenerate: "생성",
        wordListGenerating: "생성 중...",
        wordListGeneratingButton: "작업 중",
        wordListDownloadDocx: "Word",
        wordListDownloadPdf: "PDF",
        wordListDownloadHwp: "HWP",
        wordListReadyStatus: "{count}개",
        wordListPendingOutput: "...",
        wordListEmptyOutput: "단어를 붙여넣으세요.",
        wordListOutputPlaceholder: "출력",
        wordListPageLabel: "{number}페이지",
        wordListGenerationFailed: "생성에 실패했어요.",
        wordListExportFailed: "내보내기에 실패했어요.",
        playMaster1000: "Zia's Master 1000 열기",
        master1000Note: `${ZIAMEZA_IDENTITY.displayDomain}에서 한국어 핵심 단어 1,000개를 익혀요.`,
        master1000LaunchNote: `${ZIAMEZA_IDENTITY.displayDomain}의 최신 Master 1000 사이트를 열어요.`,
        master1000MissingTitle: "Zia's Master 1000은 이동했어요",
        master1000MissingBody: "{path}에서 최신 Master 1000 사이트를 열어 주세요.",
        randomTeamsModeTeams: "팀",
        randomTeamsDefaultName: "팀",
        randomTeamsGroupName: "모둠 이름",
        randomTeamsSoundOnAction: "소리 켜기",
        randomTeamsSoundOffAction: "소리 끄기",
        randomTeamsAnimationsOnAction: "애니메이션 켜기",
        randomTeamsAnimationsOffAction: "애니메이션 끄기",
        randomTeamsShowMissingAction: "결석 번호 보이기",
        randomTeamsHideMissingAction: "결석 번호 숨기기",
        randomTeamsClearAction: "결과 지우기",
        randomTeamsNamePrefix: "개",
        randomTeamsNameEnding: "만들기",
        randomTeamsMakeNamedGroups: "{groups} 만들기",
        randomTeamsNamedGroupLabel: "개 {groups}",
        randomTeamsNamedGroupSize: "{group}당 {size}명",
        randomTeamsNamedFloor: "미배정 · {group}",
        randomTeamsModeOrder: "순서",
        randomTeamsMakeTeams: "팀 만들기",
        randomTeamsMakeOrders: "순서 만들기",
        randomTeamsShuffleAgain: "다시 섞기",
        randomTeamsShuffling: "섞는 중…",
        createVerb: "랜덤",
        randomTeamsSuffix: "개 팀 만들기",
        randomOrdersSuffix: "개 순서 만들기",
        thereAre: "총",
        thereIs: "총",
        membersLabel: "명입니다.",
        playersLabel: "명입니다.",
        randomTeamsClassHas: "학급 전체 학생 수는",
        randomTeamsStudentsTotal: "명입니다.",
        randomTeamsTodayStudentCount: "오늘 참여 인원은 {count}명이에요",
        randomTeamsHereToday: "오늘 참여",
        randomTeamsAttendanceTotal: "명 전체",
        randomTeamsAttendanceMissing: "명 결석",
        randomTeamsMissingIds: "결석한 학생 번호",
        randomTeamsSourceIds: "번호",
        randomTeamsSourceNames: "이름",
        randomTeamsRosterInputLabel: "학생 이름",
        randomTeamsRosterPreview: "학생 목록",
        randomTeamsRosterSample: "민아\n12 재\n소라, 알렉스\n\n이름은 줄바꿈이나 쉼표로 구분해요.\n학생 번호는 없어도 돼요.",
        randomTeamsImportNames: "가져오기",
        randomTeamsRosterFileTypes: "TXT, CSV, TSV 또는 Excel(.xlsx) 가져오기",
        randomTeamsRosterEdit: "목록 수정",
        randomTeamsRosterDone: "완료",
        randomTeamsRosterDuplicateId: "학생 번호가 중복되었어요.",
        randomTeamsRosterInvalidId: "학생 번호는 1부터 9999까지 입력해 주세요.",
        randomTeamsRosterMissingName: "학생 번호 옆에 이름을 입력해 주세요.",
        randomTeamsRosterNoNames: "목록에서 이름을 찾지 못했어요.",
        randomTeamsRosterFileError: "파일을 읽을 수 없어요. TXT, CSV, TSV 또는 XLSX 파일을 선택해 주세요.",
        randomTeamsStudentNumber: "학생 번호 {number}",
        randomTeamsGroupCount: "{count}개 팀",
        randomTeamsOneGroup: "1개 팀",
        randomTeamsGroupLabel: "개 팀",
        randomTeamsOneGroupLabel: "개 팀",
        randomOrdersGroupLabel: "개 순서",
        randomOrdersOneGroupLabel: "개 순서",
        randomOrdersGroupCount: "{count}개 순서",
        randomOrdersOneGroup: "1개 순서",
        randomTeamsGroupSize: "팀당 {size}명",
        randomOrdersGroupSize: "순서당 {size}명",
        randomTeamSuffixSingular: "개 팀 만들기",
        randomTeamSuffixPlural: "개 팀 만들기",
        randomOrderSuffixSingular: "개 순서 만들기",
        randomOrderSuffixPlural: "개 순서 만들기",
        studentCountLabelSingular: "명입니다.",
        studentCountLabelPlural: "명입니다.",
        exceptForStudent: "제외할 학생 번호:",
        studentIdPlaceholder: "번호",
        add: "\uCD94\uAC00",
        addStudentId: "\uBC88\uD638 \uCD94\uAC00",
        addStudentIdPrompt: "\uB2A6\uAC8C \uD569\uB958\uD55C \uD559\uC0DD \uBC88\uD638 \uC785\uB825",
        removeStudentId: "\uBC88\uD638 \uC81C\uAC70",
        removeStudentIdPrompt: "\uBE7C\uB0BC \uD559\uC0DD \uBC88\uD638 \uC785\uB825",
        submitStudentId: "번호 입력",
        removeItem: "삭제",
        included: "포함",
        excluded: "제외",
        randomTeamsWaitingTitle: "시작을 눌러 주세요",
        randomTeamsReadyTitle: "자유롭게 옮겨 보세요.",
        randomTeamsFloor: "미배정",
        randomTeamsFinished: "완료",
        randomTeamsFloorHint: "팀에서 빼고 싶은 번호를 여기에 놓으세요.",
        randomTeamsExclusionError: "양수 학생 번호만 중복 없이 제외할 수 있어요.",
        randomTeamsDropHere: "여기에 놓기",
        randomTeamsDropNamesHere: "학생을 여기로 옮기기",
        randomTeamsIdsInPlay: "{count}명 배정됨",
        order: "순서",
        fullscreen: "전체 화면",
        exitFullscreen: "전체 화면 닫기",
        soundOn: "소리 켜짐",
        soundOff: "소리 꺼짐",
        animationsOn: "생성 애니메이션 켜짐",
        animationsOff: "생성 애니메이션 꺼짐",
        animationsAutoOff: "100명 초과 시 생성 애니메이션 자동 꺼짐",
        animationsReducedMotion: "동작 줄이기 설정으로 생성 애니메이션 꺼짐",
        showExcludedOnFloor: "바닥에 제외 번호 보이기",
        hideExcludedOnFloor: "바닥의 제외 번호 숨기기",
        randomTeamsFinishFront: "\uCCAB \uBC88\uD638 \uC644\uB8CC",
        wipe: "결과 지우기",
        randomTeamsLateAddInvalid: "\uC0C8 \uD559\uC0DD \uBC88\uD638\uB97C \uC591\uC218\uB85C \uC785\uB825\uD574 \uC8FC\uC138\uC694.",
        randomTeamsLateAddDuplicate: "\uADF8 \uBC88\uD638\uB294 \uC774\uBBF8 \uBC30\uC815\uB418\uC5B4 \uC788\uC5B4\uC694.",
        randomTeamsLateAddExcluded: "\uADF8 \uBC88\uD638\uB97C \uCD94\uAC00\uD558\uB824\uBA74 \uBA3C\uC800 \uC608\uC678\uC5D0\uC11C \uBE7C \uC8FC\uC138\uC694.",
        randomTeamsLateRemoveInvalid: "\uC591\uC218 \uD559\uC0DD \uBC88\uD638\uB97C \uC785\uB825\uD574 \uC8FC\uC138\uC694.",
        randomTeamsLateRemoveMissing: "\uADF8 \uBC88\uD638\uB294 \uD604\uC7AC \uBCF4\uB4DC\uC5D0 \uC5C6\uC5B4\uC694.",
        inputSomeText: "텍스트를 입력하세요",
        importTextFile: "TXT 파일 가져오기"
    }
} as const;

type CopyKey = keyof typeof COPY.en;

const KO_COPY_OVERRIDES: Partial<Record<CopyKey, string>> = {
    relicRun: "\uC720\uBB3C \uD0D0\uD5D8",
    relicRunDescription:
        "\uBB38\uC7A5\uC744 \uC678\uC6CC\uC694! \uC0AC\uC6D0 \uAE4A\uC219\uC774 \uD0D0\uD5D8\uD558\uBA70 \uC54C\uB9DE\uC740 \uB2E8\uC5B4\uB97C \uACE0\uB974\uC138\uC694.",
    pictureDetectives: "\uADF8\uB9BC \uD0D0\uC815\uB2E8",
    pictureDetectivesDescription:
        "Pictionary-style \uADF8\uB9BC \uB9DE\uD788\uAE30\uB85C, \uC218\uC0AC \uD30C\uC77C\uC758 \uB2E8\uC5B4 \uD78C\uD2B8\uB97C \uD65C\uC6A9\uD574\uC694.",
    vaultBreakers: "금고털이",
    vaultBreakersDescription: "Jeopardy-style 팀 퀴즈로, 함께 답해 교실 금고를 열어요.",
    planetHoppersDescription: "퀴즈에 답하며 행성 사이를 이동해요.",
    word: "\uB2E8\uC5B4",
    translation: "\uBC88\uC5ED",
    addWords: "\uB2E8\uC5B4 \uCD94\uAC00",
    importSourceFile: "\uD30C\uC77C \uAC00\uC838\uC624\uAE30",
    translateSource: "번역 만들기",
    translateAddMissing: "빈 번역 추가",
    translateReplaceAll: "모두 새로 번역",
    translateLoading: "번역 중...",
    translateFailed: "지금 번역하지 못했어요. 다시 시도해 주세요.",
    translateSourceChanged: "번역 중에 내용이 바뀌었어요. 다시 시도해 주세요.",
    account: "\uACC4\uC815",
    signIn: "\uB85C\uADF8\uC778",
    signOut: "\uB85C\uADF8\uC544\uC6C3",
    hostAccount: "\uD638\uC2A4\uD2B8 \uACC4\uC815",
    hostHistory: "\uD638\uC2A4\uD305 \uAE30\uB85D",
    wordLists: "\uB2E8\uC5B4 \uBAA9\uB85D",
    gameFiles: "\uC800\uC7A5\uB41C \uAC8C\uC784 \uC124\uC815",
    uploadedFiles: "\uC5C5\uB85C\uB4DC\uD55C \uD30C\uC77C",
    noHostedGamesYet: "\uC544\uC9C1 \uD638\uC2A4\uD305 \uAE30\uB85D\uC774 \uC5C6\uC5B4\uC694.",
    noWordListsYet: "\uC544\uC9C1 \uB2E8\uC5B4 \uBAA9\uB85D\uC774 \uC5C6\uC5B4\uC694.",
    noGameFilesYet: "\uC544\uC9C1 \uC800\uC7A5\uB41C \uAC8C\uC784 \uC124\uC815\uC774 \uC5C6\uC5B4\uC694.",
    noUploadedFilesYet: "\uC544\uC9C1 \uC5C5\uB85C\uB4DC\uD55C \uD30C\uC77C\uC774 \uC5C6\uC5B4\uC694.",
    sourceUploaded: "\uC5C5\uB85C\uB4DC",
    sourcePlayed: "\uC0AC\uC6A9\uB428",
    lastPlayedSource: "\uD50C\uB808\uC774 \uC124\uC815",
    googleSignInUnavailable: "Google \uB85C\uADF8\uC778\uC774 \uC544\uC9C1 \uC124\uC815\uB418\uC9C0 \uC54A\uC558\uC5B4\uC694.",
    accountPlayerCount: "{count}\uBA85",
    hostedArchive: "\uBCF4\uAD00\uD568",
    fromComputer: "\uCEF4\uD4E8\uD130",
    accountBack: "\uB4A4\uB85C",
    setupSaveCopy: "설정 저장",
    setupImport: "설정 가져오기",
    setupCopy: "설정 복사",
    setupExport: "설정 다운로드",
    setupSearch: "저장한 설정 찾기",
    setupAccountAndLocal: "이 컴퓨터 · 계정과 동기화",
    setupLocalOnly: "이 컴퓨터에 저장",
    setupSave: "저장",
    setupName: "설정 이름",
    setupSavedLocal: "이 컴퓨터에 저장했어요",
    setupSavedAccount: "계정에 저장했어요",
    setupSyncError: "계정 동기화를 할 수 없어요. 컴퓨터에 저장한 내용은 안전해요.",
    setupStorageError: "이 컴퓨터에 설정을 저장하지 못했어요.",
    setupLibraryFull: "설정은 최대 100개까지 저장할 수 있어요.",
    setupEmpty: "설정을 저장하려면 게임 내용을 추가하세요.",
    setupPrevious: "이전 초안",
    setupOpened: "설정을 불러왔고 이전 초안은 보관했어요.",
    setupOpen: "열기",
    setupClose: "닫기",
    setupChooseMode: "게임 모드를 고르면 설정이 보여요.",
    setupImportSave: "가져온 설정 저장",
    setupPaste: "Ziarcadia 설정 파일을 붙여넣으세요",
    setupInvalid: "지원하지 않거나 잘못된 설정 파일입니다.",
    setupCopied: "설정을 복사했어요",
    setupMyQuizzes: "내 퀴즈",
    setupMediaBackup: "미디어 백업",
    setupRestoreMedia: "미디어 복원",
    textInput: "\uD14D\uC2A4\uD2B8",
    tableView: "\uD45C",
    editorView: "\uD3B8\uC9D1 \uBCF4\uAE30",
    underConstruction: "\uACF5\uC0AC \uC911",
    category: "\uBD84\uB958",
    value: "\uAC00\uCE58",
    prompt: "\uBB38\uC81C",
    answer: "\uC815\uB2F5",
    info: "\uC815\uBCF4",
    hint: "\uD78C\uD2B8",
    hintReveal: "\uD78C\uD2B8 \uACF5\uAC1C",
    noTranslations: "\uBC88\uC5ED \uC5C6\uC74C",
    noHints: "\uD78C\uD2B8 \uC5C6\uC74C",
    reveal: "\uACF5\uAC1C",
    revealTimerSeconds: "\uACF5\uAC1C \uD0C0\uC774\uBA38 (\uCD08)",
    overTime: "\uCC28\uB840\uB300\uB85C",
    overTimeShort: "\uC21C\uCC28",
    allAtOnce: "\uD55C\uBC88\uC5D0",
    allAtOnceShort: "\uC804\uCCB4",
    off: "\uB044\uAE30",
    wordBank: "\uC218\uC0AC \uD30C\uC77C",
    revealCaseTranslations: "해독",
    caseTranslationsVisible: "번역 표시 중",
    caseTranslationsRecharging: "번역 충전 중",
    reviewVocabulary: "단어 복습",
    returnToGame: "게임으로 돌아가기",
    previousWord: "이전 단어",
    nextVocabularyWord: "다음 단어",
    expandCaseFile: "수사 파일 펼치기",
    placeName: "\uC774\uB984 \uC785\uB825",
    stillGuessing: "\uCD94\uCE21 \uC911",
    left: "\uCD94\uCE21 \uC911",
    adjustPoints: "\uC810\uC218 \uC870\uC815",
    addPoint: "1\uC810 \uCD94\uAC00",
    subtractPoint: "1\uC810 \uBE7C\uAE30",
    auto: "\uC790\uB3D9",
    manual: "\uC218\uB3D9",
    playerActions: "\uD50C\uB808\uC774\uC5B4 \uBA54\uB274",
    kick: "\uB0B4\uBCF4\uB0B4\uAE30",
    nextWord: "\uB2E4\uC74C \uC0AC\uAC74",
    endRoundNextWord: "\uC218\uC0AC \uB05D, \uB2E4\uC74C!",
    sameSentenceStem: "\uAC19\uC740 \uBB38\uC7A5 \uD2C0",
    makeThisDeviceController: "이 기기를 컨트롤러로 사용",
    controllerLabel: "\uCEE8\uD2B8\uB864\uB7EC",
    hostControllerPurpose:
        "\uC774 QR\uB97C \uC2A4\uCE94\uD558\uBA74 \uB2E4\uB978 \uAE30\uAE30\uC5D0\uC11C \uC0AC\uAC74 \uC9C4\uD589\uC744 \uC870\uC791\uD560 \uC218 \uC788\uC5B4\uC694.",
    waitForHostToStart: "\uD638\uC2A4\uD2B8\uAC00 \uC2DC\uC791\uD560 \uB54C\uAE4C\uC9C0 \uAE30\uB2E4\uB824 \uC8FC\uC138\uC694",
    pickWord: "\uB2E4\uC74C \uC0AC\uAC74\uC744 \uACE0\uB974\uC138\uC694",
    waitingForWord: "\uC0AC\uAC74\uC744 \uAE30\uB2E4\uB9AC\uB294 \uC911...",
    hostMustChooseNextWord: "\uD638\uC2A4\uD2B8\uAC00 \uB2E4\uC74C \uC0AC\uAC74\uC744 \uACE0\uB974\uACE0 \uC788\uC5B4\uC694.",
    waitingForWordWithPoints: "{points}\uC9DC\uB9AC \uC0AC\uAC74\uC744 \uAE30\uB2E4\uB9AC\uB294 \uC911...",
    hostMustChooseNextWordWithPoints:
        "\uD638\uC2A4\uD2B8\uAC00 {points}\uC9DC\uB9AC \uB2E4\uC74C \uC0AC\uAC74\uC744 \uACE0\uB974\uACE0 \uC788\uC5B4\uC694.",
    observeFirst: "\uBA3C\uC800 \uAD00\uCC30\uD558\uC138\uC694",
    observeCountdown: "{seconds} \uAD00\uCC30",
    speedBonus: "+{points} \uC18D\uB3C4 \uBCF4\uB108\uC2A4!",
    speedBonusCelebration: "\uC18D\uB3C4\uB85C +{points}!",
    breakthroughBonusAdvice:
        "\uB3CC\uD30C \uAE30\uD68C: \uC774 \uC0AC\uAC74\uC744 \uD574\uACB0\uD558\uBA74 \uC810\uC218 \uBCF4\uB108\uC2A4 \uAE30\uD68C\uAC00 \uC788\uC5B4\uC694.",
    breakthroughEffortAdvice:
        "\uC218\uC0AC \uAE30\uB85D \uCD94\uAC00: \uD655\uC778\uD55C \uCD94\uCE21\uB9C8\uB2E4 \uB3CC\uD30C \uAE30\uD68C\uAC00 \uB354 \uCEE4\uC838\uC694.",
    breakthroughBonusCelebration: "\uB3CC\uD30C \uBCF4\uB108\uC2A4 +{points}!",
    detectiveNeverQuits: "\uD0D0\uC815\uC740 \uD3EC\uAE30\uD558\uC9C0 \uC54A\uC544\uC694!",
    finalResults: "\uCD5C\uC885 \uACB0\uACFC",
    yourPlacement: "내 순위",
    continueToModes: "\uB2E4\uC74C \uD65C\uB3D9 \uC120\uD0DD",
    waitingForHostContinue: "\uD638\uC2A4\uD2B8 \uAE30\uB2E4\uB9AC\uB294 \uC911",
    noFinalResults: "\uC544\uC9C1 \uACB0\uACFC \uC5C6\uC74C",
    showPlayers: "\uD50C\uB808\uC774\uC5B4 \uBCF4\uAE30",
    hidePlayers: "\uD50C\uB808\uC774\uC5B4 \uC228\uAE30\uAE30",
    noPlayerResults: "\uC544\uC9C1 \uD50C\uB808\uC774\uC5B4 \uC5C6\uC74C",
    firstPlace: "1\uC704",
    secondPlace: "2\uC704",
    thirdPlace: "3\uC704",
    finalResultNoPlacement: "\uC21C\uC704 \uC5C6\uC74C",
    finalResultBestTime: "\uCD5C\uACE0 \uAE30\uB85D",
    finalResultScore: "\uC810\uC218",
    finalResultPointCount: "{count}\uC810",
    finalResultNoTime: "\uC644\uC8FC \uC5C6\uC74C",
    beatPersonalBest: "\uAC1C\uC778 \uCD5C\uACE0 \uAE30\uB85D {time}\uC744 \uAE68\uBCFC\uAE4C\uC694?",
    treasureHunting: "\uC720\uBB3C \uCC3E\uB294 \uC911..."
};

type LocalePreferenceResponse = {
    suggestedLanguage?: unknown;
};

const isLanguage = isAccountLanguage;

const readStoredValue = (key: string): string | null => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

const writeStoredValue = (key: string, value: string) => {
    try {
        localStorage.setItem(key, value);
    } catch {
        // Some embedded browsers block storage. The in-memory language still updates.
    }
};

const getStoredLanguage = (): Language | null => {
    const storedLanguage = readStoredValue(LANGUAGE_STORAGE_KEY);

    return isLanguage(storedLanguage) ? storedLanguage : null;
};

let currentLanguage: Language = getStoredLanguage() ?? "en";

const interpolate = (
    template: string,
    variables?: Record<string, string | number>
): string => {
    if (!variables) {
        return template;
    }

    return template.replace(/\{(\w+)\}/g, (_, key: string) =>
        Object.prototype.hasOwnProperty.call(variables, key) ? String(variables[key]) : ""
    );
};

export const getLanguage = (): Language => currentLanguage;

export const setLanguage = (language: Language) => {
    currentLanguage = language;
    writeStoredValue(LANGUAGE_STORAGE_KEY, language);
    document.documentElement.lang = language;
};

export const toggleLanguage = (): Language => {
    const nextLanguage: Language = currentLanguage === "en" ? "ko" : "en";
    setLanguage(nextLanguage);
    return nextLanguage;
};

const fetchSuggestedLanguage = async (): Promise<Language | null> => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), SITE_TUNING.startup.localeTimeoutMs);
    try {
        const response = await fetch("/api/locale", {
            cache: "no-store",
            credentials: "same-origin",
            signal: controller.signal
        });

        if (!response.ok) {
            return null;
        }

        const data = (await response.json()) as LocalePreferenceResponse;

        return isLanguage(data.suggestedLanguage) ? data.suggestedLanguage : null;
    } catch {
        return null;
    } finally {
        clearTimeout(timeout);
    }
};

export const initializeLanguage = async () => {
    const storedLanguage = getStoredLanguage();

    if (storedLanguage) {
        currentLanguage = storedLanguage;
        document.documentElement.lang = currentLanguage;
        return;
    }

    const suggestedLanguage = await fetchSuggestedLanguage();
    const latestStoredLanguage = getStoredLanguage();

    const language = latestStoredLanguage ?? suggestedLanguage;
    if (language) {
        setLanguage(language);
        return;
    }

    document.documentElement.lang = currentLanguage;
};

export const t = (key: CopyKey, variables?: Record<string, string | number>): string => {
    const localizedCopy =
        (currentLanguage === "ko"
            ? { ...COPY.ko, ...KO_COPY_OVERRIDES }
            : COPY[currentLanguage]) as Partial<Record<CopyKey, string>>;
    return interpolate(localizedCopy[key] ?? COPY.en[key], variables);
};

export const getLanguageToggleLabel = (): string =>
    currentLanguage === "en" ? COPY.en.switchToKorean : COPY.ko.switchToEnglish;

export const formatWordCount = (count: number): string =>
    currentLanguage === "ko"
        ? `${count} ${count === 1 ? "단어" : "단어"}`
        : `${count} ${count === 1 ? "word" : "words"}`;

export const formatRelicRunSetupSummary = (levelCount: number, wordCount: number): string =>
    currentLanguage === "ko"
        ? `${levelCount}개 레벨 · ${wordCount}개 단어`
        : `${levelCount} ${levelCount === 1 ? "level" : "levels"} - ${wordCount} ${
              wordCount === 1 ? "word" : "words"
          }`;

export const formatPictionarySetupSummary = (
    termCount: number,
    hasTranslations: boolean,
    hasHints: boolean
): string => {
    if (currentLanguage === "ko") {
        const accessorySummary =
            hasTranslations && hasHints
                ? "번역+힌트"
                : hasTranslations
                  ? "번역만"
                  : hasHints
                    ? "힌트만"
                    : "단어만";

        return `용어 ${termCount}개 · ${accessorySummary}`;
    }

    const termLabel = termCount === 1 ? "term" : "terms";
    const accessorySummary =
        hasTranslations && hasHints
            ? "trans + hints"
            : hasTranslations
              ? "trans only"
              : hasHints
                ? "hints only"
                : "words only";

    return `${termCount} ${termLabel} · ${accessorySummary}`;
};

export const formatVaultBreakerSetupSummary = (categoryCount: number, vaultCount: number): string =>
    currentLanguage === "ko"
        ? `${categoryCount}개 카테고리 · ${vaultCount}개 금고`
        : `${categoryCount} ${categoryCount === 1 ? "category" : "categories"} - ${vaultCount} ${
              vaultCount === 1 ? "vault" : "vaults"
          }`;

export const formatParticipantMeta = (connected: boolean, completed: boolean): string => {
    const presence = connected ? t("here") : t("away");

    if (!completed) {
        return presence;
    }

    return currentLanguage === "ko"
        ? `${presence} · ${t("escapedShort")}`
        : `${presence} - ${t("escapedShort")}`;
};

export const formatHereCount = (count: number): string =>
    currentLanguage === "ko" ? `${count}명 참가 중` : `${count} here`;

export const formatViewerBadge = (
    isHost: boolean,
    isController: boolean,
    name: string,
    roomCode: string
): string =>
    isHost
        ? t("viewerHost", { name })
        : isController
          ? t("viewerController", { name })
          : t("viewerPlayer", { name, roomCode });

export const localizeModeLabel = (mode: RoomMode | null): string | null =>
    mode === PLANET_HOPPERS_MODE ? "Planet Hoppers" :
    mode === RELIC_RUN_MODE
        ? t("relicRun")
        : mode === PICTURE_DETECTIVES_MODE
          ? t("pictureDetectives")
          : mode === VAULT_BREAKERS_MODE
            ? t("vaultBreakers")
            : null;

export const localizeModeDescription = (mode: RoomMode | null): string | null =>
    mode === PLANET_HOPPERS_MODE ? t("planetHoppersDescription") :
    mode === RELIC_RUN_MODE
        ? t("relicRunDescription")
        : mode === PICTURE_DETECTIVES_MODE
          ? t("pictureDetectivesDescription")
          : mode === VAULT_BREAKERS_MODE
            ? t("vaultBreakersDescription")
          : null;

const localizePictionaryAwardPart = (part: string): string => {
    const match = part.match(/^(case|never-quit|speed|breakthrough) \+(\d+)$/);

    if (!match) {
        return part;
    }

    const label = match[1];
    const points = match[2];

    switch (label) {
        case "case":
            return `사건 +${points}점`;
        case "never-quit":
            return `포기 안 함 +${points}점`;
        case "speed":
            return `속도 +${points}점`;
        case "breakthrough":
            return `돌파 +${points}점`;
        default:
            return part;
    }
};

export const localizeRuntimeText = (message: string): string => {
    if (currentLanguage === "en" || !message) {
        return message;
    }

    switch (message) {
        case "Wait for the host to start.":
            return "\uD638\uC2A4\uD2B8\uAC00 \uC2DC\uC791\uD560 \uB54C\uAE4C\uC9C0 \uAE30\uB2E4\uB824 \uC8FC\uC138\uC694.";
        case "Choose the next case when you're ready.":
            return "\uC900\uBE44\uB418\uBA74 \uB2E4\uC74C \uC0AC\uAC74\uC744 \uACE0\uB974\uC138\uC694.";
        case "Waiting for the host controller to choose the next case.":
            return "\uD638\uC2A4\uD2B8 \uCEE8\uD2B8\uB864\uB7EC\uAC00 \uB2E4\uC74C \uC0AC\uAC74\uC744 \uACE0\uB974\uB294 \uC911\uC774\uC5D0\uC694.";
        case "Waiting for the next case.":
            return "\uB2E4\uC74C \uC0AC\uAC74\uC744 \uAE30\uB2E4\uB9AC\uB294 \uC911\uC774\uC5D0\uC694.";
        case "Paste a Vault Breakers board to unlock Start.":
            return "금고털이 보드를 붙여넣으면 시작할 수 있어요.";
        case "The host is setting up Vault Breakers.":
            return "호스트가 금고털이를 준비하고 있어요.";
        case "Roll a chooser, then pick a vault.":
            return "선택할 학생을 뽑고 금고를 고르세요.";
        case "Pick the next vault.":
            return "다음 금고를 고르세요.";
        case "Pick the Final Vault.":
            return "최후의 금고를 고르세요.";
        case "One last heist!":
            return "마지막 한탕!";
        case "Show the final results.":
            return "최종 결과를 보여 주세요.";
        case "Final Vault complete.":
            return "최후의 금고가 끝났어요.";
        case "Waiting for the next vault.":
            return "다음 금고를 기다리는 중이에요.";
        case "Arm a vault code.":
            return "금고 코드를 골라요.";
        case "Code armed.":
            return "코드를 잠갔어요.";
        case "Vault cracked.":
            return "금고를 열었어요.";
        case "Final Vault missed.":
            return "최후의 금고를 놓쳤어요.";
        case "No code armed.":
            return "고른 코드가 없어요.";
        case "Effort logged.":
        case "Signal logged.":
            return "노력으로 기록됐어요.";
        case "Scanner warming up.":
            return "스캐너 준비 중이에요.";
        case "That crack is resolving.":
            return "이번 시도가 확인 중이에요.";
        case "Waiting for the next crack.":
            return "다음 시도를 기다리는 중이에요.";
        case "That code is not on your scanner.":
            return "그 코드는 스캐너에 없어요.";
        case "Not it.":
            return "\uD2C0\uB838\uC5B4\uC694.";
        case "Connecting...":
            return "연결 중...";
        case "Creating room...":
            return "방 만드는 중...";
        case "Connection lost. Reconnecting...":
            return "연결이 끊어졌어요. 다시 연결하는 중...";
        case "The room connection is not ready.":
            return "방 연결이 아직 준비되지 않았어요.";
        case "Room code must be 1 letter and 1 number.":
            return "방 코드는 영문 1자와 숫자 1자여야 해요.";
        case "Enter a valid room code.":
            return "유효한 방 코드를 입력해 주세요.";
        case "Enter your name before joining.":
            return "참가하기 전에 이름을 입력해 주세요.";
        case "Could not reconnect to the room.":
            return "방에 다시 연결할 수 없었어요.";
        case "Could not join the room.":
            return "방에 참가할 수 없었어요.";
        case "Could not create the room.":
            return "방을 만들 수 없었어요.";
        case "Could not open the host room.":
            return "호스트 방을 열 수 없었어요.";
        case "Copy failed. Your browser did not allow clipboard access.":
            return "복사에 실패했어요. 브라우저가 클립보드 접근을 허용하지 않았어요.";
        case "Host link copied.":
            return "호스트 링크를 복사했어요.";
        case "Join link copied.":
            return "참가 링크를 복사했어요.";
        case "Room code copied.":
            return "방 코드를 복사했어요.";
        case "Request failed.":
            return "요청에 실패했어요.";
        case "Room not found.":
            return "방을 찾을 수 없어요.";
        case "This room is full.":
            return "이 방은 이미 가득 찼어요.";
        case "Could not create a room right now.":
            return "지금은 방을 만들 수 없어요.";
        case "Not Found":
            return "찾을 수 없어요.";
        case "Select Relic Run, paste your text, and start whenever you are ready.":
            return "보물 탐험을 고르고 텍스트를 붙여넣은 뒤 준비되면 시작하세요.";
        case "Choose a mode and start whenever you are ready.":
            return "모드를 고르고 준비되면 시작하세요.";
        case "Paste the passage for Relic Run to unlock Start.":
            return "보물 탐험을 시작하려면 문장을 붙여넣으세요.";
        case "Add word pairs to unlock Picture Detectives.":
            return "그림 탐정단을 시작하려면 단어 짝을 추가하세요.";
        case "Need a host controller before Picture Detectives can start.":
            return "그림 탐정단을 시작하려면 호스트 컨트롤러가 필요해요.";
        case "The host is setting up Relic Run.":
            return "호스트가 보물 탐험을 설정하고 있어요.";
        case "The host is setting up Picture Detectives.":
            return "호스트가 그림 탐정단을 설정하고 있어요.";
        case "Waiting for the host to choose a mode.":
            return "호스트가 모드를 고르는 중이에요.";
        case "The relic run is live.":
            return "보물 탐험이 진행 중이에요.";
        case "Treasure secured.":
            return "보물을 확보했어요.";
        case "The relic run is not active.":
            return "보물 탐험이 아직 진행 중이 아니에요.";
        case "You can only replay after finishing.":
            return "완료한 뒤에만 다시 할 수 있어요.";
        case "The replay has already been used.":
            return "다시 하기를 이미 사용했어요.";
        case "Select Relic Run and paste text before starting.":
            return "시작하기 전에 보물 탐험을 선택하고 텍스트를 붙여넣으세요.";
        case "Picture Detectives is not active.":
            return "그림 탐정단이 아직 진행 중이 아니에요.";
        case "Only teams can guess right now.":
            return "지금은 팀만 추측할 수 있어요.";
        case "Waiting for the host controller to pick a case.":
            return "호스트 컨트롤러가 사건을 고르는 중이에요.";
        case "You already solved this case.":
            return "이 사건은 이미 해결했어요.";
        case "Observe the drawing first.":
            return "\uBA3C\uC800 \uADF8\uB9BC\uC744 \uAD00\uCC30\uD558\uC138\uC694.";
        case "No tries left this round.":
            return "이번 라운드는 기회를 다 썼어요.";
        case "No tries left. Wait a moment.":
            return "\uAE30\uD68C\uB97C \uB2E4 \uC37C\uC5B4\uC694. \uC7A0\uAE50\uB9CC \uAE30\uB2E4\uB824 \uC8FC\uC138\uC694.";
        case "Effort noted.":
            return "\uB178\uB825\uC740 \uAE30\uB85D\uB410\uC5B4\uC694.";
        case "Wait a moment before guessing again.":
            return "다시 추측하기 전에 잠깐 기다려 주세요.";
        case "Type a guess first.":
            return "먼저 추측을 입력하세요.";
        case "That guess is already checked.":
            return "이미 확인한 추측이에요.";
        case "Check the spelling and try again.":
            return "철자를 확인하고 다시 시도하세요.";
        case "That guess isn't in the case file. Check your spelling.":
            return "수사 파일에 없는 추측이에요. 철자를 확인해 보세요.";
        case "The case got muddled! Keep trying for half points!":
            return "사건이 흐려졌어요! 계속하면 절반 점수를 받을 수 있어요!";
        case "Case solved!":
            return "해결했어요!";
        case "Detective work noted.":
            return "탐정 노력이 기록됐어요.";
        case "A detective never quits!":
            return t("detectiveNeverQuits");
        case "Host controller ready. Wait for the host to start.":
            return "호스트 컨트롤러가 준비됐어요. 호스트가 시작할 때까지 기다리세요.";
        case "Waiting for the controller seat.":
            return "컨트롤러 자리를 기다리는 중이에요.";
        case "Choose the first case when you're ready.":
            return "준비되면 첫 사건을 고르세요.";
        case "Draw the selected case word.":
            return "선택한 사건 단어를 그려 보세요.";
        case "Guess the drawing.":
            return "그림을 보고 맞혀 보세요.";
        case "You got it.":
            return "맞혔어요.";
        case "That case selection was not understood.":
            return "그 사건 선택은 이해하지 못했어요.";
        case "That drawing stroke was not understood.":
            return "그 그리기 선은 이해하지 못했어요.";
        case "Only the host controller can advance the case.":
            return "호스트 컨트롤러만 다음 사건으로 넘어갈 수 있어요.";
        case "Only the host controller can preview the next case points.":
            return "호스트 컨트롤러만 다음 사건 배점을 미리 보여줄 수 있어요.";
        case "That point preview was not understood.":
            return "그 배점 미리 보기는 이해하지 못했어요.";
        case "Choose a valid case before continuing.":
            return "계속하기 전에 올바른 사건을 고르세요.";
        case "Only the host can draw here.":
            return "여기서는 호스트만 그림을 그릴 수 있어요.";
        case "Only the host can undo drawing strokes.":
            return "호스트만 그리기 선을 되돌릴 수 있어요.";
        case "Only the host can clear the canvas.":
            return "호스트만 캔버스를 지울 수 있어요.";
        case "Only the host can change room settings.":
            return "호스트만 방 설정을 바꿀 수 있어요.";
        case "The room is already live.":
        case "The room is already active.":
            return "방이 이미 진행 중이에요.";
        case "Relic Run is not selected.":
            return "유물 탐험이 선택되지 않았어요.";
        case "Only the host can start the room.":
            return "호스트만 방을 시작할 수 있어요.";
        case "Paste a Vault Breakers board before starting.":
            return "시작하기 전에 금고털이 보드를 붙여넣으세요.";
        case "Choose a mode before starting.":
            return "시작하기 전에 모드를 고르세요.";
        case "Only the host can end the room.":
            return "호스트만 게임을 끝낼 수 있어요.";
        case "The room is not active.":
            return "방이 아직 진행 중이 아니에요.";
        case "Results are still being saved. Please try again shortly.":
            return "결과를 저장하고 있어요. 잠시 후 다시 시도해 주세요.";
        case "Turn on host participation before replaying.":
            return "다시 플레이하려면 호스트 참여를 켜세요.";
        case "Only the host can adjust Picture Detectives scores.":
            return "호스트만 그림 탐정단 점수를 조정할 수 있어요.";
        case "Choose a player before adjusting points.":
            return "점수를 조정할 플레이어를 고르세요.";
        case "Only the host can choose a vault.":
            return "호스트만 금고를 고를 수 있어요.";
        case "Finish the current vault first.":
            return "먼저 현재 금고를 마무리하세요.";
        case "Choose an unlocked vault.":
            return "선택할 수 있는 금고를 고르세요.";
        case "Vault Breakers is not active.":
            return "금고털이가 아직 진행 중이 아니에요.";
        case "Only teams can arm a vault code.":
            return "팀 플레이어만 금고 코드를 고를 수 있어요.";
        case "Only the host can advance the crack.":
            return "호스트만 다음 시도로 넘어갈 수 있어요.";
        case "Choose a vault first.":
            return "먼저 금고를 고르세요.";
        case "Only the host can finish a vault.":
            return "호스트만 금고를 마무리할 수 있어요.";
        case "Only the host can roll the chooser.":
            return "호스트만 선택자를 뽑을 수 있어요.";
        case "Only the host can arm the Final Vault.":
            return "호스트만 최후의 금고를 준비할 수 있어요.";
        case "Only the host can toggle the Final Vault.":
            return "호스트만 최후의 금고를 켜거나 끌 수 있어요.";
        case "The Final Vault can only be turned off from the board.":
            return "최후의 금고는 보드 화면에서만 끌 수 있어요.";
        case "The Final Vault has already started.":
            return "최후의 금고가 이미 시작됐어요.";
        case "The Final Vault is already armed.":
            return "최후의 금고가 이미 준비됐어요.";
        case "No vault remains for the final heist.":
            return "마지막 한탕에 쓸 금고가 남아 있지 않아요.";
        case "The Final Vault chooser is locked.":
            return "최후의 금고 선택자는 바꿀 수 없어요.";
        case "The Final Vault timer is locked at 30 seconds.":
            return "최후의 금고 시간은 30초로 고정돼요.";
        case "The Final Vault is already complete.":
            return "최후의 금고가 이미 끝났어요.";
        case "The whole bank has been heisted.":
            return "은행을 전부 털었어요.";
        case "Only the host can unlock Vault Breakers values.":
            return "호스트만 다음 금고 금액을 열 수 있어요.";
        case "Only the host can adjust the Vault Breakers timer.":
            return "호스트만 금고털이 제한 시간을 조정할 수 있어요.";
        case "Only the host can adjust Vault Breakers scores.":
            return "호스트만 금고털이 점수를 조정할 수 있어요.";
        case "Final results are up.":
            return "최종 결과가 나왔어요.";
        case "Waiting for the host to continue.":
            return "호스트가 계속하기를 기다리는 중이에요.";
        case "That room code is already in use.":
            return "이미 사용 중인 방 코드예요.";
        case "Could not switch controller rooms.":
            return "컨트롤러 방을 바꿀 수 없었어요.";
        case "FileReader is unavailable.":
            return "이 브라우저에서는 파일을 읽을 수 없어요.";
        case "Could not read the file.":
            return "파일을 읽을 수 없었어요.";
        case "Could not read the file as a spreadsheet.":
            return "스프레드시트 파일을 읽을 수 없었어요.";
        case "Sign in first.":
            return "먼저 로그인해 주세요.";
        case "Invalid account session.":
            return "계정 세션이 올바르지 않아요. 다시 로그인해 주세요.";
        case "Account mismatch.":
            return "로그인한 계정이 일치하지 않아요.";
        case "Could not load account.":
            return "계정을 불러올 수 없었어요.";
        case "Could not sign in.":
        case "Could not sign in with Google.":
            return "로그인할 수 없었어요.";
        case "Could not load Google sign-in.":
        case "Could not reach Google sign-in.":
            return "Google 로그인을 불러올 수 없었어요.";
        case "Google sign-in is not configured yet.":
            return "Google 로그인이 아직 설정되지 않았어요.";
        case "Could not sign out.":
            return "로그아웃할 수 없었어요.";
        case "Google sign-in did not return a valid credential.":
        case "Google sign-in returned an invalid token.":
        case "Google sign-in returned an unsupported token.":
        case "Google sign-in could not be verified.":
        case "Google sign-in is for a different client.":
        case "Google sign-in came from an unknown issuer.":
            return "Google 로그인 정보를 확인할 수 없어요.";
        case "Google sign-in keys changed. Try again.":
            return "Google 로그인 정보가 변경됐어요. 다시 시도해 주세요.";
        case "Google sign-in expired. Try again.":
            return "Google 로그인이 만료됐어요. 다시 시도해 주세요.";
        case "Use a Gmail or Google Workspace account.":
            return "Gmail 또는 Google Workspace 계정을 사용해 주세요.";
        case "Choose a mode first.":
            return "먼저 모드를 고르세요.";
        case "Add words first.":
            return "먼저 단어를 추가하세요.";
        case "Paste at least one term first.":
            return "먼저 단어를 하나 이상 붙여넣으세요.";
        case "Add a custom template first.":
            return "먼저 사용자 지정 양식을 입력하세요.";
        case "The word-list generator is not configured yet.":
            return "단어 목록 생성기가 아직 설정되지 않았어요.";
        case "Could not generate the word list right now.":
            return "지금은 단어 목록을 만들 수 없어요.";
        case "OpenAI returned an empty word-list response.":
            return "AI가 단어 목록 결과를 보내지 않았어요.";
        case "Could not create the PDF canvas.":
            return "PDF 미리보기를 만들 수 없었어요.";
        case "Autotranslator is not configured yet.":
            return "자동 번역기가 아직 설정되지 않았어요.";
        case "Could not start Autotranslator right now.":
            return "지금은 자동 번역기를 시작할 수 없어요.";
        case "Nothing to translate yet.":
            return "아직 번역할 내용이 없어요.";
        case "Could not translate that line right now.":
            return "지금은 그 문장을 번역할 수 없어요.";
        case "OpenAI returned an empty realtime token.":
            return "실시간 번역 연결 정보를 받지 못했어요.";
        case "The AI assistant is not configured yet.":
            return "AI 안내가 아직 설정되지 않았어요.";
        case "Could not reach the AI assistant right now.":
            return "지금은 AI 안내에 연결할 수 없어요.";
        case "OpenAI returned an empty answer.":
            return "AI가 답변을 보내지 않았어요.";
        case "Invalid Autotranslator origin.":
        case "Invalid account origin.":
        case "Invalid chat origin.":
        case "Invalid game balance origin.":
        case "Invalid room creation payload.":
        case "Invalid sign-in origin.":
        case "Invalid sign-out origin.":
        case "Invalid word-list origin.":
            return "요청을 확인할 수 없어요. 페이지를 새로고침해 주세요.";
        case "Method Not Allowed":
            return "지원하지 않는 요청이에요.";
        case "That move was not understood.":
            return "그 이동은 이해하지 못했어요.";
        case "The room received an invalid message.":
            return "방이 잘못된 메시지를 받았어요.";
        default:
            break;
    }

    const readyMatch = message.match(/^Ready to start Relic Run with (\d+) words\.$/);

    if (readyMatch) {
        return `${readyMatch[1]}개 단어로 보물 탐험을 시작할 준비가 되었어요.`;
    }

    const multiLevelReadyMatch = message.match(
        /^Ready to start Relic Run with (\d+) levels and (\d+) words\.$/
    );

    if (multiLevelReadyMatch) {
        return `${multiLevelReadyMatch[1]}개 레벨, ${multiLevelReadyMatch[2]}개 단어로 보물 탐험을 시작할 준비가 되었어요.`;
    }

    const pictionaryReadyMatch = message.match(/^Ready to start Picture Detectives with (\d+) case entries\.$/);

    if (pictionaryReadyMatch) {
        return `수사 항목 ${pictionaryReadyMatch[1]}개로 그림 탐정단을 시작할 준비가 되었어요.`;
    }

    const vaultReadyMatch = message.match(/^Ready to start Vault Breakers with (\d+) vaults\.$/);

    if (vaultReadyMatch) {
        return `${vaultReadyMatch[1]}개 금고로 금고털이를 시작할 준비가 되었어요.`;
    }

    const controllerLockedMatch = message.match(/^Host controller locked to (.+)\.$/);

    if (controllerLockedMatch) {
        return `\uD638\uC2A4\uD2B8 \uCEE8\uD2B8\uB864\uB7EC\uAC00 ${controllerLockedMatch[1]}\uB2D8\uC5D0\uAC8C \uC5F0\uACB0\uB418\uC5B4 \uC788\uC5B4\uC694.`;
    }

    const vaultCrackingMatch = message.match(/^Cracking (.+) for ([^.]+)\.$/);

    if (vaultCrackingMatch) {
        return `${vaultCrackingMatch[1]}의 ${vaultCrackingMatch[2]} 금고를 여는 중이에요.`;
    }

    const watchingPlayerMatch = message.match(/^Watching (.+)\.$/);

    if (watchingPlayerMatch) {
        return `${watchingPlayerMatch[1]} 관전 중이에요.`;
    }

    const vaultEffortLoggedMatch = message.match(/^(?:Effort|Signal) logged \+\$([0-9,]+)\.$/);

    if (vaultEffortLoggedMatch) {
        return `노력 +$${vaultEffortLoggedMatch[1]}.`;
    }

    const vaultCrackedFeedbackMatch = message.match(
        /^(Final Vault cracked|Massive Loot cracked|Diamond vault cracked|Vault cracked) \+\$([0-9,]+)\.((?: (?:Growing Stash|Diamond|Quick lock|Comeback) \+\$[0-9,]+\.)*)$/
    );

    if (vaultCrackedFeedbackMatch) {
        const headline =
            vaultCrackedFeedbackMatch[1] === "Final Vault cracked"
                ? "최후의 금고 열기 성공"
                : vaultCrackedFeedbackMatch[1] === "Massive Loot cracked"
                ? "대박 보물 금고 열기 성공"
                : vaultCrackedFeedbackMatch[1] === "Diamond vault cracked"
                  ? "다이아몬드 금고 열기 성공"
                  : "금고 열기 성공";
        const detailLabels: Record<string, string> = {
            "Growing Stash": "쌓이는 돈다발",
            Diamond: "다이아몬드",
            "Quick lock": "빠른 잠금",
            Comeback: "추격"
        };
        const details = [...vaultCrackedFeedbackMatch[3].matchAll(
            / (Growing Stash|Diamond|Quick lock|Comeback) \+\$([0-9,]+)\./g
        )].map((match) => `${detailLabels[match[1]]} +$${match[2]}`);

        return `${headline} +$${vaultCrackedFeedbackMatch[2]}!${details.length > 0 ? ` ${details.join(" · ")}` : ""}`;
    }

    const currentWordMatch = message.match(/^Current (?:word|case): (.+)$/);

    if (currentWordMatch) {
        return `현재 사건: ${currentWordMatch[1]}`;
    }

    const solvedCaseBreakdownMatch = message.match(/^Case solved! \+(\d+) total: (.+)\.$/);

    if (solvedCaseBreakdownMatch) {
        const awardParts = solvedCaseBreakdownMatch[2]
            .split(", ")
            .map((part) => localizePictionaryAwardPart(part))
            .join(", ");

        return `사건 해결! 총 +${solvedCaseBreakdownMatch[1]}점: ${awardParts}.`;
    }

    const solvedCaseSingleAwardMatch = message.match(
        /^Case solved! \+(\d+) (case point(?:s)?|never-quit point(?:s)?|speed bonus|breakthrough bonus)\.$/
    );

    if (solvedCaseSingleAwardMatch) {
        const points = solvedCaseSingleAwardMatch[1];
        const label = solvedCaseSingleAwardMatch[2];
        const translatedLabel = label.startsWith("case")
            ? "사건"
            : label.startsWith("never-quit")
              ? "포기 안 함"
              : label.startsWith("speed")
                ? "속도"
                : "돌파";

        return `사건 해결! ${translatedLabel} +${points}점.`;
    }

    const effortAwardMatch = message.match(/^Casework \+(\d+) effort point(?:s)?\.$/);

    if (effortAwardMatch) {
        return `수사 노력 +${effortAwardMatch[1]}점.`;
    }

    const solvedCaseWithSpeedBonusMatch = message.match(
        /^You solved the case for (\d+) point(?:s)?! And a \+(\d+) speed bonus!$/
    );

    const solvedCaseWithBreakthroughMatch = message.match(
        /^You solved the case for (\d+) point(?:s)?!(?: And a \+(\d+) speed bonus!)? (?:Breakthrough|Lucky break) \+(\d+)!$/
    );

    if (solvedCaseWithBreakthroughMatch) {
        const speedBonusPoints = solvedCaseWithBreakthroughMatch[2];
        const breakthroughPoints = solvedCaseWithBreakthroughMatch[3];

        return speedBonusPoints
            ? `사건 해결! ${solvedCaseWithBreakthroughMatch[1]}점이에요. 속도 보너스 +${speedBonusPoints}, 돌파 보너스 +${breakthroughPoints}!`
            : `사건 해결! ${solvedCaseWithBreakthroughMatch[1]}점이에요. 돌파 보너스 +${breakthroughPoints}!`;
    }

    if (solvedCaseWithSpeedBonusMatch) {
        return `사건 해결! ${solvedCaseWithSpeedBonusMatch[1]}점이에요. 그리고 속도 보너스 +${solvedCaseWithSpeedBonusMatch[2]}!`;
    }

    const solvedCaseMatch = message.match(/^You solved the case for (\d+) point(?:s)?!$/);

    if (solvedCaseMatch) {
        return `사건 해결! ${solvedCaseMatch[1]}점이에요.`;
    }

    const correctForPointsWithSpeedBonusMatch = message.match(
        /^Correct for (\d+) point(?:s)?\. \+(\d+) speed bonus!$/
    );

    if (correctForPointsWithSpeedBonusMatch) {
        return `${correctForPointsWithSpeedBonusMatch[1]}\uC810 \uC815\uB2F5\uC774\uC5D0\uC694. +${correctForPointsWithSpeedBonusMatch[2]} \uC18D\uB3C4 \uBCF4\uB108\uC2A4!`;
    }

    const correctForPointsMatch = message.match(/^Correct for (\d+) point(?:s)?\.$/);

    if (correctForPointsMatch) {
        return `${correctForPointsMatch[1]}점 정답이에요.`;
    }

    const noTriesEffortMatch = message.match(/^No tries left\. Effort \+(\d+) point(?:s)?\.$/);

    if (noTriesEffortMatch) {
        return `\uAE30\uD68C\uB97C \uB2E4 \uC37C\uC5B4\uC694. \uB178\uB825 +${noTriesEffortMatch[1]}\uC810.`;
    }

    const effortPointsMatch = message.match(/^Effort \+(\d+) point(?:s)?\.$/);

    if (effortPointsMatch) {
        return `\uB178\uB825 +${effortPointsMatch[1]}\uC810.`;
    }

    const detectiveWorkPointsMatch = message.match(/^Your detective work earned (\d+) point(?:s)?\.$/);

    if (detectiveWorkPointsMatch) {
        return `탐정 노력으로 ${detectiveWorkPointsMatch[1]}점을 얻었어요.`;
    }

    const livesLeftMatch = message.match(/^(\d+) tries left\.$/);

    if (livesLeftMatch) {
        return `기회 ${livesLeftMatch[1]}번 남았어요.`;
    }

    const firstWordMatch = message.match(/^Find the first (?:word|case): (.+)$/);

    if (firstWordMatch) {
        return `첫 사건을 해결하세요: ${firstWordMatch[1]}`;
    }

    const nextWordMatch = message.match(/^Find the next (?:word|case): (.+)$/);

    if (nextWordMatch) {
        return `다음 사건을 해결하세요: ${nextWordMatch[1]}`;
    }

    if (/^(?:OpenAI request failed|OpenAI HTTP)(?::|\s|\.)/.test(message)) {
        return "AI 요청에 실패했어요. 잠시 후 다시 시도해 주세요.";
    }

    return message;
};
